import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminManagement from '../components/AdminManagement';
import MasterUsersManagement from '../components/MasterUsersManagement';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Shield,
  Server,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  MoreVertical,
  Settings,
  UserPlus,
  Download,
  Filter,
  Search,
  RefreshCw,
  Globe,
  Building,
  CreditCard,
  Zap,
  Target,
  AlertCircle,
  Plus,
  Trash2,
  Ban,
  CheckSquare,
  UserCheck,
  Database,
  Wifi,
  Mail,
  Smartphone,
  Monitor,
  HardDrive,
  Cpu,
  MemoryStick,
  Save,
  X,
  Calendar,
  MapPin,
  Phone,
  ExternalLink,
  FileText,
  Key,
  Lock,
  Unlock,
  UserX,
  ArrowUpRight,
  Star,
  TrendingDownIcon,
  ToggleLeft,
  ToggleRight,
  Briefcase,
  Home,
  Ticket,
  Layout,
  Image
} from 'lucide-react';
import { useMasterAuth } from '../hooks/useMasterAuth';
import { useAuth } from '../contexts/AuthContext';
import { adminAccountsApi } from '../services/api/adminAccountsApi';
import { 
  MasterDashboardStats,
  PlatformAccount,
  PlatformAnalytics,
  SystemHealth,
  PlatformFinancials,
  PlatformUser,
  SupportTicket,
  MasterRealtimeUpdate,
  AuditLog
} from '../types/master';
import { CustomerCheckoutData } from '../types/admin';
import { customerService } from '../services/customerService';
import { exportFilteredCustomerData } from '../utils/exportUtils';
import { eventsService } from '../services/eventsService';
import { generateTicketPDF, generateTicketNumber, generateQRCodeData, TicketData } from '../utils/ticketGenerator';
import { TICKET_CATEGORIES } from '../constants/ticketCategories';
import { Card, Button, LoadingSpinner, EmptyState, Input } from '../components/ui';

const MasterDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { 
    isMaster, 
    isPlatformAdmin, 
    canAccessMasterDashboard,
    canManageAccounts,
    canViewFinancials,
    canManageSystem,
    canAccessAnalytics,
    canManageSupport,
    canAccessAuditLogs,
    canCreateEvents,
    canEditEvents,
    canDeleteEvents,
    canIssueTickets,
    canAccessDatabase,
    canManageMasterUsers,
    getCurrentMasterRole,
    loading: authLoading
  } = useMasterAuth();
  const { isAuthenticated, user } = useAuth();
  
  // State management
  const [stats, setStats] = useState<MasterDashboardStats | null>(null);
  const [accounts, setAccounts] = useState<PlatformAccount[]>([]);
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [financials, setFinancials] = useState<PlatformFinancials | null>(null);
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [realtimeUpdates, setRealtimeUpdates] = useState<MasterRealtimeUpdate[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'analytics' | 'system' | 'financials' | 'support' | 'customers' | 'admin-management' | 'master-users' | 'ticket-generation' | 'create-event' | 'add-venue'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('all');
  const [adminAccounts, setAdminAccounts] = useState<any[]>([]);

  // Customer Management States
  const [allCustomerData, setAllCustomerData] = useState<CustomerCheckoutData[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerCheckoutData[]>([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string>('all');
  const [customerStatusFilter, setCustomerStatusFilter] = useState<'all' | 'info_filled' | 'order_placed' | 'payment_completed'>('all');

  // Ticket Generation States
  const [availableEvents, setAvailableEvents] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedEventForTicket, setSelectedEventForTicket] = useState<string>('');
  const [ticketForm, setTicketForm] = useState({
    customerName: '',
    customerPhone: '',
    ticketType: 'Full Access',
    quantity: 1,
    price: 0,
    seatInfo: ''
  });
  const [generatingTicket, setGeneratingTicket] = useState(false);

  // Event Management States
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [editingEvent, setEditingEvent] = useState<any | null>(null);
  const [selectedAdminAccountId, setSelectedAdminAccountId] = useState<string>('');
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    endDate: '',
    time: '',
    venue: '',
    venueId: '',
    category: 'Music',
    price: 0,
    capacity: 0,
    image: '',
    tags: [] as string[],
    status: 'draft' as 'draft' | 'published' | 'sold_out' | 'cancelled',
    duration: '',
    dresscode: '',
    gallery: [] as string[],
    location: '',
    sections: [{ name: '', price: 0, seats: 0, benefits: [] as string[], description: '' }] as Array<{ name: string; price: number; seats: number; benefits: string[]; description: string }>
  });

  // Venue Management States
  const [allVenues, setAllVenues] = useState<any[]>([]);
  const [editingVenue, setEditingVenue] = useState<any | null>(null);
  const [newVenue, setNewVenue] = useState({
    name: '',
    capacity: 0,
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    parkingInfo: '',
    accessibilityInfo: '',
    googleMapsUrl: ''
  });

  // Modal states
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<PlatformAccount | null>(null);
  const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
  const [editingTicket, setEditingTicket] = useState<SupportTicket | null>(null);

  // Form states
  const [newAccount, setNewAccount] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    type: 'client' as 'admin' | 'client',
    subscriptionPlan: 'basic' as 'basic' | 'pro' | 'enterprise' | 'custom',
    industry: '',
    website: '',
    address: {
      street: '',
      city: '',
      country: 'Jordan',
      zipCode: ''
    },
    commissionRate: 5.0
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'client' as 'admin' | 'client' | 'master' | 'platform_admin',
    accountId: '',
    password: '',
    permissions: [] as string[]
  });

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    category: 'technical' as 'technical' | 'billing' | 'account' | 'feature_request',
    accountId: '',
    assignedTo: ''
  });

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    initializeMasterData();
    loadAdminAccounts();
  }, []);

  // Calculate real stats whenever data changes
  useEffect(() => {
    if (adminAccounts.length > 0 || allEvents.length > 0 || allCustomerData.length > 0) {
      calculateRealStats();
    }
  }, [adminAccounts, allEvents, allCustomerData]);

  // Calculate system health whenever data changes or on mount
  useEffect(() => {
    if (adminAccounts.length >= 0) { // Calculate even if empty to test API
      calculateSystemHealth();
    }
  }, [adminAccounts, allEvents, allCustomerData]);

  // Calculate platform analytics from real data
  const calculatePlatformAnalytics = () => {
    const now = new Date();
    const months: string[] = [];
    const monthlyData: { [key: string]: { accounts: number; revenue: number; events: number; tickets: number } } = {};

    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      months.push(monthKey);
      monthlyData[monthKey] = { accounts: 0, revenue: 0, events: 0, tickets: 0 };
    }

    // Calculate overview
    const totalAccounts = adminAccounts.length;
    const activeAccounts = adminAccounts.filter(acc => acc.isActive || !acc.status).length;
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newAccountsThisMonth = adminAccounts.filter(acc => {
      const createdAt = new Date(acc.createdAt || acc.subscription?.startDate || '');
      return createdAt >= startOfMonth;
    }).length;

    const totalEvents = allEvents.length;
    const totalTicketsSold = allCustomerData.reduce((sum, customer) => {
      const orderData = (customer as any).orderData;
      if (orderData && orderData.quantity) {
        return sum + (typeof orderData.quantity === 'number' ? orderData.quantity : parseInt(orderData.quantity) || 0);
      }
      return sum;
    }, 0);

    const totalRevenue = allCustomerData.reduce((sum, customer) => {
      const orderData = (customer as any).orderData;
      if (orderData && orderData.total) {
        return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
      }
      return sum;
    }, 0);

    const commissionRate = 0.05;
    const totalCommissionEarned = totalRevenue * commissionRate;
    const averageAccountValue = totalAccounts > 0 ? totalRevenue / totalAccounts : 0;

    // Calculate monthly trends
    adminAccounts.forEach(account => {
      const createdAt = new Date(account.createdAt || account.subscription?.startDate || '');
      const monthKey = createdAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].accounts++;
      }
    });

    allEvents.forEach(event => {
      const createdAt = new Date(event.createdAt || event.date || '');
      const monthKey = createdAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].events++;
      }
    });

    allCustomerData.forEach(customer => {
      const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
      const monthKey = submittedAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (monthlyData[monthKey]) {
        const orderData = (customer as any).orderData;
        if (orderData) {
          if (orderData.total) {
            monthlyData[monthKey].revenue += typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0;
          }
          if (orderData.quantity) {
            monthlyData[monthKey].tickets += typeof orderData.quantity === 'number' ? orderData.quantity : parseInt(orderData.quantity) || 0;
          }
        }
      }
    });

    // Build monthly metrics with change calculations
    const accountGrowth: any[] = months.map((month, index) => {
      const value = monthlyData[month].accounts;
      const prevValue = index > 0 ? monthlyData[months[index - 1]].accounts : 0;
      const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
      return { month, value, change };
    });

    const revenueGrowth: any[] = months.map((month, index) => {
      const value = monthlyData[month].revenue;
      const prevValue = index > 0 ? monthlyData[months[index - 1]].revenue : 0;
      const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
      return { month, value, change };
    });

    const eventGrowth: any[] = months.map((month, index) => {
      const value = monthlyData[month].events;
      const prevValue = index > 0 ? monthlyData[months[index - 1]].events : 0;
      const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
      return { month, value, change };
    });

    const ticketSalesGrowth: any[] = months.map((month, index) => {
      const value = monthlyData[month].tickets;
      const prevValue = index > 0 ? monthlyData[months[index - 1]].tickets : 0;
      const change = prevValue > 0 ? ((value - prevValue) / prevValue) * 100 : 0;
      return { month, value, change };
    });

    // Calculate top performers
    const accountsWithMetrics = adminAccounts.map(account => {
      const accountEvents = allEvents.filter(evt => (evt as any).adminAccountId === account.id || (evt as any).companyName === account.companyName);
      const accountCustomers = allCustomerData.filter(customer => customer.eventId && accountEvents.some(evt => evt.id === customer.eventId));
      const accountRevenue = accountCustomers.reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);
      const accountTickets = accountCustomers.reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.quantity) {
          return sum + (typeof orderData.quantity === 'number' ? orderData.quantity : parseInt(orderData.quantity) || 0);
        }
        return sum;
      }, 0);

      return {
        ...account,
        metrics: {
          totalEvents: accountEvents.length,
          totalRevenue: accountRevenue,
          totalTicketsSold: accountTickets,
          averageEventSize: accountEvents.length > 0 ? accountTickets / accountEvents.length : 0,
          monthlyActiveUsers: accountCustomers.length,
          conversionRate: accountTickets > 0 ? (accountRevenue / accountTickets) * 100 : 0,
          lastEventDate: accountEvents.length > 0 ? accountEvents.sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())[0].date : undefined
        },
        financial: {
          commissionRate: 5.0,
          totalCommissionEarned: accountRevenue * 0.05,
          outstandingBalance: 0,
          payoutMethod: 'bank_transfer' as const
        }
      } as PlatformAccount;
    });

    const topByRevenue = [...accountsWithMetrics].sort((a, b) => b.metrics.totalRevenue - a.metrics.totalRevenue);
    const topByEvents = [...accountsWithMetrics].sort((a, b) => b.metrics.totalEvents - a.metrics.totalEvents);
    const topByGrowth = [...accountsWithMetrics].sort((a, b) => b.metrics.conversionRate - a.metrics.conversionRate);

    // Geographic breakdown (if available)
    const accountsByCountry: any[] = [];
    const countryMap: { [key: string]: { count: number; revenue: number } } = {};
    
    adminAccounts.forEach(account => {
      const country = (account as any).address?.country || (account as any).businessInfo?.address?.country || 'Unknown';
      if (!countryMap[country]) {
        countryMap[country] = { count: 0, revenue: 0 };
      }
      countryMap[country].count++;
      
      const accountEvents = allEvents.filter(evt => (evt as any).adminAccountId === account.id || (evt as any).companyName === account.companyName);
      const accountCustomers = allCustomerData.filter(customer => customer.eventId && accountEvents.some(evt => evt.id === customer.eventId));
      const accountRevenue = accountCustomers.reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);
      countryMap[country].revenue += accountRevenue;
    });

    Object.entries(countryMap).forEach(([country, data]) => {
      accountsByCountry.push({
        country,
        count: data.count,
        revenue: data.revenue,
        percentage: totalAccounts > 0 ? (data.count / totalAccounts) * 100 : 0
      });
    });

    // Industry breakdown (if available)
    const industryMap: { [key: string]: { accounts: number; revenue: number; events: number } } = {};
    
    adminAccounts.forEach(account => {
      const industry = (account as any).businessInfo?.industry || (account as any).industry || 'Other';
      if (!industryMap[industry]) {
        industryMap[industry] = { accounts: 0, revenue: 0, events: 0 };
      }
      industryMap[industry].accounts++;
      
      const accountEvents = allEvents.filter(evt => (evt as any).adminAccountId === account.id || (evt as any).companyName === account.companyName);
      industryMap[industry].events += accountEvents.length;
      
      const accountCustomers = allCustomerData.filter(customer => customer.eventId && accountEvents.some(evt => evt.id === customer.eventId));
      const accountRevenue = accountCustomers.reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);
      industryMap[industry].revenue += accountRevenue;
    });

    const industryBreakdown: any[] = Object.entries(industryMap).map(([industry, data]) => ({
      industry,
      accounts: data.accounts,
      revenue: data.revenue,
      events: data.events,
      percentage: totalAccounts > 0 ? (data.accounts / totalAccounts) * 100 : 0
    }));

    setAnalytics({
      overview: {
        totalAccounts,
        activeAccounts,
        newAccountsThisMonth,
        totalPlatformRevenue: totalRevenue,
        totalCommissionEarned,
        totalEventsCreated: totalEvents,
        totalTicketsSold,
        averageAccountValue
      },
      trends: {
        accountGrowth,
        revenueGrowth,
        eventGrowth,
        ticketSalesGrowth
      },
      topPerformers: {
        byRevenue: topByRevenue,
        byEvents: topByEvents,
        byGrowth: topByGrowth
      },
      geographic: {
        accountsByCountry,
        revenueByCountry: accountsByCountry
      },
      industryBreakdown
    });
  };

  // Calculate analytics whenever data changes
  useEffect(() => {
    if (adminAccounts.length >= 0 || allEvents.length >= 0 || allCustomerData.length >= 0) {
      calculatePlatformAnalytics();
    }
  }, [adminAccounts, allEvents, allCustomerData]);

  // Reload data when company selection changes
  useEffect(() => {
    if (activeTab === 'customers' || activeTab === 'create-event' || activeTab === 'add-venue' || activeTab === 'ticket-generation') {
      loadEventsForTicketGeneration();
      if (activeTab === 'customers') {
        loadAllCustomerData();
      }
      if (activeTab === 'create-event') {
        loadAllEvents();
      }
    }
  }, [selectedCompanyName, activeTab]);

  const loadAdminAccounts = async () => {
    try {
      const response = await adminAccountsApi.getAll();
      setAdminAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading admin accounts:', error);
    }
  };

  // Calculate real system health from actual data
  const calculateSystemHealth = async () => {
    try {
      // Test API response time
      const apiStartTime = window.performance.now();
      try {
        await adminAccountsApi.getAll();
        const apiResponseTime = Math.round(window.performance.now() - apiStartTime);
        const apiStatus = apiResponseTime < 500 ? 'operational' : apiResponseTime < 1000 ? 'degraded' : 'offline';
        
        // Test database (via events API)
        const dbStartTime = window.performance.now();
        await eventsService.getAllEvents();
        const dbResponseTime = Math.round(window.performance.now() - dbStartTime);
        const dbStatus = dbResponseTime < 200 ? 'operational' : dbResponseTime < 500 ? 'degraded' : 'offline';
        
        // Calculate uptime based on successful operations
        // Assume high uptime if APIs are responding
        const uptime = apiStatus === 'operational' && dbStatus === 'operational' ? 99.9 : 
                      apiStatus === 'degraded' || dbStatus === 'degraded' ? 98.5 : 95.0;
        
        // Calculate performance based on response times
        // Lower response times = higher performance
        const avgResponseTime = (apiResponseTime + dbResponseTime) / 2;
        const performance = Math.max(0, Math.min(100, 100 - (avgResponseTime / 10)));
        
        // Calculate active alerts based on system issues
        let activeAlerts = 0;
        const inactiveAccounts = adminAccounts.filter(acc => !acc.isActive).length;
        if (inactiveAccounts > 0) activeAlerts += 1;
        
        if (apiStatus !== 'operational') activeAlerts += 1;
        if (dbStatus !== 'operational') activeAlerts += 1;
        
        // Calculate data usage metrics (based on actual data)
        const totalAccounts = adminAccounts.length;
        const totalEvents = allEvents.length;
        const totalCustomers = allCustomerData.length;
        
        // Estimate metrics based on data volume
        // These are approximations since we don't have real server metrics
        const estimatedCpu = Math.min(100, Math.max(10, (totalAccounts * 2 + totalEvents * 0.5 + totalCustomers * 0.1)));
        const estimatedMemory = Math.min(100, Math.max(20, (totalAccounts * 3 + totalEvents * 1 + totalCustomers * 0.2)));
        const estimatedDisk = Math.min(100, Math.max(5, (totalAccounts * 1 + totalEvents * 2 + totalCustomers * 0.5)));
        const estimatedNetwork = Math.min(100, Math.max(10, (totalAccounts * 1.5 + totalEvents * 1.5 + totalCustomers * 0.3)));
        
        // Determine overall status
        const status = uptime >= 99.5 && performance >= 90 && activeAlerts === 0 ? 'healthy' :
                      uptime >= 98 && performance >= 80 && activeAlerts <= 2 ? 'warning' : 'critical';
        
        // Get last incident date (use oldest inactive account or oldest event as reference)
        let lastIncident = 'N/A';
        if (inactiveAccounts > 0) {
          const oldestInactive = adminAccounts
            .filter(acc => !acc.isActive)
            .sort((a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime())[0];
          if (oldestInactive?.createdAt) {
            lastIncident = new Date(oldestInactive.createdAt).toISOString();
          }
        }
        
        setSystemHealth({
          status,
          uptime: Math.round(uptime * 100) / 100,
          performance: Math.round(performance * 10) / 10,
          activeAlerts,
          lastIncident,
          components: {
            api: { 
              status: apiStatus, 
              responseTime: apiResponseTime 
            },
            database: { 
              status: dbStatus, 
              responseTime: dbResponseTime 
            },
            payments: { 
              status: 'operational', // Assume operational if no payment errors
              responseTime: Math.round(apiResponseTime * 1.2) // Estimate slightly higher
            },
            cdn: { 
              status: 'operational', // CDN is typically operational
              responseTime: Math.round(apiResponseTime * 0.6) // CDN is typically faster
            },
            monitoring: { 
              status: 'operational', 
              responseTime: Math.round(apiResponseTime * 0.5) // Monitoring is internal
            }
          },
          metrics: {
            cpu: Math.round(estimatedCpu),
            memory: Math.round(estimatedMemory),
            disk: Math.round(estimatedDisk),
            network: Math.round(estimatedNetwork)
          }
        });
      } catch (error) {
        // If API calls fail, set degraded status
        setSystemHealth({
          status: 'critical',
          uptime: 95.0,
          performance: 50.0,
          activeAlerts: 3,
          lastIncident: new Date().toISOString(),
          components: {
            api: { status: 'offline', responseTime: 5000 },
            database: { status: 'offline', responseTime: 5000 },
            payments: { status: 'offline', responseTime: 5000 },
            cdn: { status: 'degraded', responseTime: 2000 },
            monitoring: { status: 'operational', responseTime: 100 }
          },
          metrics: {
            cpu: 50,
            memory: 50,
            disk: 50,
            network: 50
          }
        });
      }
    } catch (error) {
      console.error('Error calculating system health:', error);
    }
  };

  // Calculate real stats from actual data
  const calculateRealStats = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    // Calculate accounts stats from adminAccounts
    const totalAccounts = adminAccounts.length;
    const activeAccounts = adminAccounts.filter(acc => acc.status === 'active' || !acc.status).length;
    const suspendedAccounts = adminAccounts.filter(acc => acc.status === 'suspended').length;
    const newAccountsThisMonth = adminAccounts.filter(acc => {
      const createdAt = new Date(acc.createdAt || acc.subscription?.startDate || '');
      return createdAt >= startOfMonth;
    }).length;

    // Calculate events stats from allEvents
    const totalEvents = allEvents.length;
    const activeEvents = allEvents.filter(evt => {
      const eventDate = new Date(evt.date || evt.createdAt || '');
      return eventDate >= now;
    }).length;
    const eventsThisMonth = allEvents.filter(evt => {
      const createdAt = new Date(evt.createdAt || evt.date || '');
      return createdAt >= startOfMonth;
    }).length;
    const avgEventsPerAccount = totalAccounts > 0 ? (totalEvents / totalAccounts) : 0;

    // Calculate users and revenue stats from allCustomerData
    const totalUsers = allCustomerData.length;
    const activeUsers = allCustomerData.filter(customer => 
      customer.status === 'payment_completed' || customer.status === 'order_placed'
    ).length;
    const newUsersToday = allCustomerData.filter(customer => {
      const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
      return submittedAt >= startOfToday;
    }).length;

    // Calculate revenue from customer data
    const totalRevenue = allCustomerData.reduce((sum, customer) => {
      const orderData = (customer as any).orderData;
      if (orderData && orderData.total) {
        return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
      }
      return sum;
    }, 0);

    // Calculate monthly revenue
    const monthlyRevenue = allCustomerData
      .filter(customer => {
        const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
        return submittedAt >= startOfMonth;
      })
      .reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);

    // Calculate last month revenue for growth calculation
    const lastMonthRevenue = allCustomerData
      .filter(customer => {
        const submittedAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
        return submittedAt >= lastMonth && submittedAt < startOfMonth;
      })
      .reduce((sum, customer) => {
        const orderData = (customer as any).orderData;
        if (orderData && orderData.total) {
          return sum + (typeof orderData.total === 'number' ? orderData.total : parseFloat(orderData.total) || 0);
        }
        return sum;
      }, 0);

    // Calculate revenue growth percentage
    const revenueGrowth = lastMonthRevenue > 0 
      ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    // Calculate commissions (assuming 5% commission rate)
    const commissionRate = 0.05;
    const totalCommissions = totalRevenue * commissionRate;
    const monthlyCommissions = monthlyRevenue * commissionRate;

    // Calculate engagement (percentage of users who completed payment)
    const engagement = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

    // System health (can be kept as default or calculated from available metrics)
    const systemUptime = 99.97; // Can be calculated from actual system metrics if available
    const systemPerformance = 94.2; // Can be calculated from actual system metrics if available
    const systemAlerts = 0; // Can be calculated from actual system alerts if available

    setStats({
      accounts: {
        total: totalAccounts,
        active: activeAccounts,
        new: newAccountsThisMonth,
        suspended: suspendedAccounts
      },
      revenue: {
        total: totalRevenue,
        monthly: monthlyRevenue,
        commissions: totalCommissions,
        growth: Math.round(revenueGrowth * 10) / 10 // Round to 1 decimal place
      },
      events: {
        total: totalEvents,
        active: activeEvents,
        thisMonth: eventsThisMonth,
        avgPerAccount: Math.round(avgEventsPerAccount * 10) / 10 // Round to 1 decimal place
      },
      users: {
        total: totalUsers,
        active: activeUsers,
        newToday: newUsersToday,
        engagement: Math.round(engagement * 10) / 10 // Round to 1 decimal place
      },
      system: {
        uptime: systemUptime,
        performance: systemPerformance,
        alerts: systemAlerts,
        lastIncident: 'N/A'
      }
    });
  };

  const initializeMasterData = () => {
    // Load data first, then calculate stats
    loadAllCustomerData();
    loadEventsForTicketGeneration();
    loadAllEvents();
    loadAllVenues();

    setAccounts([
      {
        id: '1',
        type: 'client',
        businessName: 'Jordan Events Co.',
        contactName: 'Ahmed Al-Zahra',
        email: 'ahmed@jordanevents.com',
        phone: '+962-6-123-4567',
        status: 'active',
        subscriptionPlan: 'pro',
        createdAt: '2024-01-15T10:00:00Z',
        lastActiveAt: '2024-01-20T15:30:00Z',
        businessInfo: {
          industry: 'Entertainment',
          website: 'https://jordanevents.com',
          address: {
            street: '123 Rainbow St',
            city: 'Amman',
            country: 'Jordan',
            zipCode: '11183'
          }
        },
        metrics: {
          totalEvents: 25,
          totalRevenue: 147680,
          totalTicketsSold: 3247,
          averageEventSize: 129.88,
          monthlyActiveUsers: 1234,
          conversionRate: 18.7,
          lastEventDate: '2024-01-19T20:00:00Z'
        },
        financial: {
          commissionRate: 5.5,
          totalCommissionEarned: 8122.4,
          outstandingBalance: 2340.50,
          payoutMethod: 'bank_transfer'
        },
        compliance: {
          kycStatus: 'approved',
          riskLevel: 'low',
          lastReviewDate: '2024-01-10T09:00:00Z'
        }
      },
      {
        id: '2',
        type: 'admin',
        businessName: 'Amman Conference Center',
        contactName: 'Sarah Mahmoud',
        email: 'sarah@ammanconference.jo',
        phone: '+962-6-987-6543',
        status: 'active',
        subscriptionPlan: 'enterprise',
        createdAt: '2024-01-10T14:00:00Z',
        lastActiveAt: '2024-01-20T09:15:00Z',
        businessInfo: {
          industry: 'Business Events',
          website: 'https://ammanconference.jo',
          address: {
            street: '456 Business District',
            city: 'Amman',
            country: 'Jordan',
            zipCode: '11194'
          }
        },
        metrics: {
          totalEvents: 18,
          totalRevenue: 89340,
          totalTicketsSold: 1876,
          averageEventSize: 104.22,
          monthlyActiveUsers: 890,
          conversionRate: 22.1,
          lastEventDate: '2024-01-18T18:00:00Z'
        },
        financial: {
          commissionRate: 4.5,
          totalCommissionEarned: 4020.30,
          outstandingBalance: 1250.75,
          payoutMethod: 'bank_transfer'
        },
        compliance: {
          kycStatus: 'approved',
          riskLevel: 'low',
          lastReviewDate: '2024-01-12T11:00:00Z'
        }
      },
      {
        id: '3',
        type: 'client',
        businessName: 'Petra Music Festival',
        contactName: 'Omar Hassan',
        email: 'omar@petramusicfest.com',
        phone: '+962-3-215-9876',
        status: 'suspended',
        subscriptionPlan: 'basic',
        createdAt: '2024-01-05T08:30:00Z',
        lastActiveAt: '2024-01-18T12:45:00Z',
        businessInfo: {
          industry: 'Music & Arts',
          website: 'https://petramusicfest.com',
          address: {
            street: '789 Cultural St',
            city: 'Petra',
            country: 'Jordan',
            zipCode: '77110'
          }
        },
        metrics: {
          totalEvents: 8,
          totalRevenue: 23450,
          totalTicketsSold: 892,
          averageEventSize: 111.5,
          monthlyActiveUsers: 456,
          conversionRate: 15.3,
          lastEventDate: '2024-01-16T19:30:00Z'
        },
        financial: {
          commissionRate: 6.0,
          totalCommissionEarned: 1407.0,
          outstandingBalance: 0,
          payoutMethod: 'paypal'
        },
        compliance: {
          kycStatus: 'requires_update',
          riskLevel: 'medium',
          lastReviewDate: '2024-01-08T16:00:00Z'
        }
      }
    ]);

    setPlatformUsers([
      {
        id: '1',
        name: 'Ahmed Al-Zahra',
        email: 'ahmed@jordanevents.com',
        role: 'client',
        accountId: '1',
        isActive: true,
        lastLogin: '2024-01-20T15:30:00Z',
        createdAt: '2024-01-15T10:00:00Z',
        permissions: ['events.create', 'events.edit', 'analytics.view']
      },
      {
        id: '2',
        name: 'Sarah Mahmoud',
        email: 'sarah@ammanconference.jo',
        role: 'admin',
        accountId: '2',
        isActive: true,
        lastLogin: '2024-01-20T09:15:00Z',
        createdAt: '2024-01-10T14:00:00Z',
        permissions: ['events.create', 'events.edit', 'users.manage', 'analytics.view', 'financials.view']
      },
      {
        id: '3',
        name: 'Omar Hassan',
        email: 'omar@petramusicfest.com',
        role: 'client',
        accountId: '3',
        isActive: false,
        lastLogin: '2024-01-18T12:45:00Z',
        createdAt: '2024-01-05T08:30:00Z',
        permissions: ['events.create', 'events.edit']
      }
    ]);

    setSupportTickets([
      {
        id: '1',
        title: 'Payment Processing Issue',
        description: 'Unable to process credit card payments for event tickets',
        status: 'open',
        priority: 'high',
        category: 'technical',
        accountId: '1',
        createdBy: 'ahmed@jordanevents.com',
        assignedTo: 'support@etickets.com',
        createdAt: '2024-01-20T14:30:00Z',
        updatedAt: '2024-01-20T16:45:00Z',
        tags: ['payment', 'urgent', 'credit-card']
      },
      {
        id: '2',
        title: 'Commission Rate Inquiry',
        description: 'Request to discuss enterprise pricing and commission rates',
        status: 'pending',
        priority: 'medium',
        category: 'billing',
        accountId: '2',
        createdBy: 'sarah@ammanconference.jo',
        assignedTo: 'billing@etickets.com',
        createdAt: '2024-01-19T11:20:00Z',
        updatedAt: '2024-01-19T11:20:00Z',
        tags: ['pricing', 'enterprise', 'commission']
      },
      {
        id: '3',
        title: 'Account Verification Documents',
        description: 'Need to update KYC documents for compliance',
        status: 'resolved',
        priority: 'medium',
        category: 'account',
        accountId: '3',
        createdBy: 'omar@petramusicfest.com',
        assignedTo: 'compliance@etickets.com',
        createdAt: '2024-01-16T09:10:00Z',
        updatedAt: '2024-01-18T14:30:00Z',
        tags: ['kyc', 'compliance', 'documents'],
        resolvedAt: '2024-01-18T14:30:00Z'
      }
    ]);

    // System health will be calculated from real data
    calculateSystemHealth();

    setLoadingData(false);
  };

  // Load all events
  const loadAllEvents = async () => {
    try {
      const companyName = selectedCompanyName !== 'all' ? selectedCompanyName : undefined;
      const events = await eventsService.getAllEvents(undefined, companyName);
      console.log('Loaded events:', events);
      setAllEvents(events || []);
    } catch (error) {
      console.error('Error loading events:', error);
      setAllEvents([]);
    }
  };

  // Load all venues from API
  const loadAllVenues = async () => {
    try {
      const { venuesApi } = await import('../services/api');
      const response = await venuesApi.getAllVenues();
      
      // Handle response format
      let venues = [];
      if (Array.isArray(response)) {
        venues = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        venues = response.data;
      } else if (response && response.venues && Array.isArray(response.venues)) {
        venues = response.venues;
      }
      
      setAllVenues(venues);
    } catch (error) {
      console.error('Error loading venues:', error);
      setAllVenues([]);
    }
  };

  // Load events for ticket generation
  const loadEventsForTicketGeneration = async () => {
    try {
      const companyName = selectedCompanyName !== 'all' ? selectedCompanyName : undefined;
      const events = await eventsService.getAllEvents(undefined, companyName);
      setAvailableEvents(events);
    } catch (error) {
      console.error('Error loading events for ticket generation:', error);
    }
  };

  // Event Management Functions
  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.endDate) {
      alert('Please fill in title, from date, and to date (required fields)');
      return;
    }

    if (newEvent.endDate < newEvent.date) {
      alert('End date must be on or after start date');
      return;
    }

    if (!selectedAdminAccountId) {
      alert('Please select a company name for this event');
      return;
    }

    // Validate sections
    if (!newEvent.sections || newEvent.sections.length === 0) {
      alert('Please add at least one ticket section');
      return;
    }

    if (newEvent.sections.some(s => !s.name || !s.name.trim() || s.price < 0 || s.seats <= 0)) {
      alert('Please ensure all sections have a name, non-negative price, and positive number of seats.');
      return;
    }

    try {
      // Format date as range string
      const dateRange = newEvent.endDate && newEvent.endDate !== newEvent.date 
        ? `${newEvent.date} to ${newEvent.endDate}`
        : newEvent.date;
      
      const eventPayload: any = {
        ...newEvent,
        date: dateRange,
        status: newEvent.status || 'draft',
        adminId: selectedAdminAccountId
      };
      // Remove endDate from payload as it's now part of date string
      delete eventPayload.endDate;
      // Add sections separately as they're not part of the Event type
      if (newEvent.sections && newEvent.sections.length > 0) {
        eventPayload.sections = newEvent.sections.map(s => ({ ...s, benefits: s.benefits || [], description: s.description || '' }));
      }
      const result = await eventsService.addEvent(eventPayload);
      
      console.log('Event created successfully:', result);
      
      await loadAllEvents();
      await loadEventsForTicketGeneration();
      
      setNewEvent({
        title: '',
        description: '',
        date: '',
        endDate: '',
        time: '',
        venue: '',
        venueId: '',
        category: 'Music',
        price: 0,
        capacity: 0,
        image: '',
        tags: [],
        status: 'draft',
        duration: '',
        dresscode: '',
        gallery: [],
        location: '',
        sections: [{ name: '', price: 0, seats: 0, benefits: [], description: '' }]
      });
      
      setSelectedAdminAccountId('');
      setEditingEvent(null);
      
      alert('Event created successfully!');
    } catch (error: any) {
      console.error('Error creating event:', error);
      const errorMessage = error?.message || error?.response?.data?.message || error?.response?.data?.errors?.[0]?.message || 'Unknown error';
      alert(`Error creating event: ${errorMessage}`);
    }
  };

  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    // Parse date range if it contains " to "
    let startDate = event.date || '';
    let endDate = '';
    if (startDate.includes(' to ')) {
      const parts = startDate.split(' to ');
      startDate = parts[0];
      endDate = parts[1];
    } else {
      endDate = startDate; // If no range, use same date for both
    }
    
    setNewEvent({
      title: event.title,
      description: event.description || '',
      date: startDate,
      endDate: endDate,
      time: event.time || '',
      venue: event.venue || '',
      venueId: event.venueId || '',
      category: event.category || 'Music',
      price: event.price || 0,
      capacity: event.capacity || 0,
      image: event.image || '',
      tags: event.tags || [],
      status: event.status || 'draft',
      duration: event.duration || '',
      dresscode: event.dresscode || '',
      gallery: event.gallery && Array.isArray(event.gallery) ? event.gallery : [],
      location: event.location || '',
      sections: event.sections && event.sections.length > 0 
        ? event.sections.map((s: any) => ({ 
            name: s.name, 
            price: s.price, 
            seats: s.seats,
            benefits: s.benefits && Array.isArray(s.benefits) ? s.benefits : [],
            description: s.description || s.benefits?.join(', ') || ''
          }))
        : [{ name: '', price: 0, seats: 0, benefits: [], description: '' }]
    });
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    if (!newEvent.date || !newEvent.endDate) {
      alert('Please fill in from date and to date (required fields)');
      return;
    }

    if (newEvent.endDate < newEvent.date) {
      alert('End date must be on or after start date');
      return;
    }

    // Validate sections
    if (!newEvent.sections || newEvent.sections.length === 0) {
      alert('Please add at least one ticket section');
      return;
    }

    if (newEvent.sections.some(s => !s.name || !s.name.trim() || s.price < 0 || s.seats <= 0)) {
      alert('Please ensure all sections have a name, non-negative price, and positive number of seats.');
      return;
    }

    try {
      // Format date as range string
      const dateRange = newEvent.endDate && newEvent.endDate !== newEvent.date 
        ? `${newEvent.date} to ${newEvent.endDate}`
        : newEvent.date;
      
      const eventPayload: any = {
        ...newEvent,
        date: dateRange,
        duration: newEvent.duration || undefined,
        dresscode: newEvent.dresscode || undefined,
        gallery: newEvent.gallery && newEvent.gallery.length > 0 ? newEvent.gallery : undefined,
        location: newEvent.location || undefined
      };
      // Remove endDate from payload as it's now part of date string
      delete eventPayload.endDate;
      // Add sections separately as they're not part of the Event type
      if (newEvent.sections && newEvent.sections.length > 0) {
        eventPayload.sections = newEvent.sections.map(s => ({
          name: s.name,
          price: s.price,
          seats: s.seats,
          benefits: s.benefits && s.benefits.length > 0 ? s.benefits : [],
          description: s.description || ''
        }));
      }
      await eventsService.updateEvent(editingEvent.id, eventPayload);
      await loadAllEvents();
      await loadEventsForTicketGeneration();
      
      setEditingEvent(null);
      setNewEvent({
        title: '',
        description: '',
        date: '',
        endDate: '',
        time: '',
        venue: '',
        venueId: '',
        category: 'Music',
        price: 0,
        capacity: 0,
        image: '',
        tags: [],
        status: 'draft',
        duration: '',
        dresscode: '',
        gallery: [],
        location: '',
        sections: [{ name: '', price: 0, seats: 0, benefits: [], description: '' }]
      });
      
      alert('Event updated successfully!');
    } catch (error: any) {
      console.error('Error updating event:', error);
      alert(`Error updating event: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await eventsService.deleteEvent(eventId);
      await loadAllEvents();
      await loadEventsForTicketGeneration();
      alert('Event deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting event:', error);
      alert(`Error deleting event: ${error.message || 'Unknown error'}`);
    }
  };

  const handlePublishEvent = async (eventId: string) => {
    try {
      await eventsService.publishEvent(eventId);
      await loadAllEvents();
      await loadEventsForTicketGeneration();
      alert('Event published successfully! It will now appear on the website.');
    } catch (error: any) {
      console.error('Error publishing event:', error);
      alert(`Error publishing event: ${error.message || 'Unknown error'}`);
    }
  };

  // Venue Management Functions
  const handleCreateVenue = async () => {
    if (!newVenue.name || newVenue.capacity <= 0) {
      alert('Please fill in venue name and capacity');
      return;
    }

    try {
      const { venuesApi } = await import('../services/api');
      const response = await venuesApi.createVenue({
        name: newVenue.name,
        capacity: newVenue.capacity,
        address: newVenue.address || undefined,
        city: newVenue.city || undefined,
        country: newVenue.country || undefined,
        phone: newVenue.phone || undefined,
        email: newVenue.email || undefined,
        website: newVenue.website || undefined,
        description: newVenue.description || undefined,
        parkingInfo: newVenue.parkingInfo || undefined,
        accessibilityInfo: newVenue.accessibilityInfo || undefined,
        googleMapsUrl: newVenue.googleMapsUrl || undefined
      });

      const createdVenue = response.venue || response.data || response;
      alert('Venue created successfully!');
      await loadAllVenues();
      
      setNewVenue({
        name: '',
        capacity: 0,
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        parkingInfo: '',
        accessibilityInfo: '',
        googleMapsUrl: ''
      });
    } catch (error: any) {
      console.error('Error creating venue:', error);
      console.error('Error details:', {
        error,
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
        fullError: JSON.stringify(error, null, 2)
      });
      
      // Extract error message from various possible locations
      // The API client interceptor throws the response.data object directly
      // which has structure: { success: false, message: "...", errors: [...] }
      let errorMessage = 'Unknown error';
      
      // Check if error is a string
      if (typeof error === 'string') {
        errorMessage = error;
      }
      // Check if error has a message property (most common case from API)
      else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      // Check if error.response.data has a message (Axios error structure)
      else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      // Check for errors array in the error object
      else if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
        const firstError = error.errors[0];
        errorMessage = typeof firstError === 'string' ? firstError : (firstError.message || JSON.stringify(firstError));
      }
      // Check if error is the response data object directly (from API client interceptor)
      else if (error && typeof error === 'object') {
        // Try to get message property
        if (error.message) {
          errorMessage = error.message;
        }
        // Try to stringify safely, avoiding [object Object]
        else {
          try {
            const errorStr = JSON.stringify(error, null, 2);
            // If it's a valid JSON, use it; otherwise use a generic message
            if (errorStr && errorStr !== '{}' && errorStr !== 'null') {
              errorMessage = `Error: ${errorStr}`;
            } else {
              errorMessage = 'Failed to create venue. Please check the console for details.';
            }
          } catch (e) {
            errorMessage = 'Failed to create venue. Please check the console for details.';
          }
        }
      }
      
      alert(`Error creating venue: ${errorMessage}`);
    }
  };

  const handleEditVenue = (venue: any) => {
    setEditingVenue(venue);
    setNewVenue({
      name: venue.name || '',
      capacity: venue.capacity || 0,
      address: venue.address || '',
      city: venue.city || '',
      country: venue.country || '',
      phone: venue.phone || '',
      email: venue.email || '',
      website: venue.website || '',
      description: venue.description || '',
      parkingInfo: venue.parkingInfo || '',
      accessibilityInfo: venue.accessibilityInfo || '',
      googleMapsUrl: venue.googleMapsUrl || ''
    });
  };

  const handleUpdateVenue = async () => {
    if (!editingVenue) return;

    if (!newVenue.name || !newVenue.capacity) {
      alert('Please fill in all required fields for venue.');
      return;
    }

    try {
      const { venuesApi } = await import('../services/api');
      const response = await venuesApi.updateVenue(editingVenue.id, {
        name: newVenue.name,
        capacity: newVenue.capacity,
        address: newVenue.address || undefined,
        city: newVenue.city || undefined,
        country: newVenue.country || undefined,
        phone: newVenue.phone || undefined,
        email: newVenue.email || undefined,
        website: newVenue.website || undefined,
        description: newVenue.description || undefined,
        parkingInfo: newVenue.parkingInfo || undefined,
        accessibilityInfo: newVenue.accessibilityInfo || undefined,
        googleMapsUrl: newVenue.googleMapsUrl || undefined
      });

      alert('Venue updated successfully!');
      await loadAllVenues();
      
      setEditingVenue(null);
      setNewVenue({
        name: '',
        capacity: 0,
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        parkingInfo: '',
        accessibilityInfo: '',
        googleMapsUrl: ''
      });
    } catch (error: any) {
      console.error('Error updating venue:', error);
      alert(`Error updating venue: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteVenue = async (venueId: string) => {
    if (!window.confirm('Are you sure you want to delete this venue?')) return;

    try {
      const { venuesApi } = await import('../services/api');
      await venuesApi.deleteVenue(venueId);
      alert('Venue deleted successfully!');
      await loadAllVenues();
    } catch (error: any) {
      console.error('Error deleting venue:', error);
      alert(`Error deleting venue: ${error.message || 'Unknown error'}`);
    }
  };


  // Handle ticket generation
  const handleGenerateTicket = async () => {
    if (!selectedEventForTicket || !ticketForm.customerName || !ticketForm.customerPhone) {
      alert('Please fill in all required fields');
      return;
    }

    setGeneratingTicket(true);

    try {
      const event = availableEvents.find(e => e.id === selectedEventForTicket);
      if (!event) {
        throw new Error('Event not found');
      }

      // Generate tickets for the quantity
      const tickets: TicketData[] = [];
      const ticketNumbers: string[] = [];

      for (let i = 0; i < ticketForm.quantity; i++) {
        const ticketNumber = generateTicketNumber();
        ticketNumbers.push(ticketNumber);

        const ticket: TicketData = {
          ticketNumber,
          eventTitle: event.title,
          eventDate: new Date(event.date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          eventTime: event.time || 'TBA',
          eventLocation: event.venue || 'TBA',
          eventVenue: event.venue || 'TBA',
          category: event.category || 'General',
          ticketType: ticketForm.ticketType,
          price: ticketForm.price,
          customerName: ticketForm.customerName,
          customerPhone: ticketForm.customerPhone,
          seatInfo: ticketForm.seatInfo || undefined,
          qrCode: generateQRCodeData(ticketNumber, event.id)
        };

        tickets.push(ticket);
      }

      // Generate PDF
      let pdfBlob: Blob;
      if (tickets.length === 1) {
        pdfBlob = await generateTicketPDF(tickets[0]);
      } else {
        // Generate multiple tickets in one PDF
        const { generateMultipleTicketsPDF } = await import('../utils/ticketGenerator');
        pdfBlob = await generateMultipleTicketsPDF(tickets);
      }

      // Download PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticketNumbers[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Save to customer data
      const orderData = {
        ticketType: ticketForm.ticketType,
        quantity: ticketForm.quantity,
        price: ticketForm.price,
        subtotal: ticketForm.price * ticketForm.quantity,
        total: ticketForm.price * ticketForm.quantity,
        ticketNumbers: ticketNumbers,
        generatedAt: new Date().toISOString()
      };

      // Save customer data
      await customerService.saveCustomerData({
        firstName: ticketForm.customerName.split(' ')[0] || ticketForm.customerName,
        lastName: ticketForm.customerName.split(' ').slice(1).join(' ') || '',
        email: '',
        phone: ticketForm.customerPhone,
        eventId: event.id,
        eventTitle: event.title,
        paymentMethod: 'cash',
        status: 'payment_completed',
        isCompleted: true,
        orderData: orderData,
        billingAddress: '',
        city: '',
        country: 'Jordan',
        zipCode: ''
      });

      // Reload customer data
      await loadAllCustomerData();

      // Reset form
      setTicketForm({
        customerName: '',
        customerPhone: '',
        ticketType: 'General Admission',
        quantity: 1,
        price: event.price || 0,
        seatInfo: ''
      });

      alert(`Ticket${tickets.length > 1 ? 's' : ''} generated and saved successfully!`);
    } catch (error: any) {
      console.error('Error generating ticket:', error);
      alert(`Error generating ticket: ${error.message || 'Unknown error'}`);
    } finally {
      setGeneratingTicket(false);
    }
  };

  // Customer Management Functions
  const loadAllCustomerData = async () => {
    try {
      const companyName = selectedCompanyName !== 'all' ? selectedCompanyName : undefined;
      const customerData = await customerService.getAllCustomerData(undefined, companyName);
      setAllCustomerData(customerData);
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  };

  const filterCustomerData = () => {
    let filtered = [...allCustomerData];

    // Filter by event
    if (selectedEventId !== 'all') {
      filtered = filtered.filter(customer => customer.eventId === selectedEventId);
    }

    // Filter by status
    if (customerStatusFilter !== 'all') {
      filtered = filtered.filter(customer => customer.status === customerStatusFilter);
    }

    // Filter by search query
    if (customerSearchQuery.trim()) {
      const query = customerSearchQuery.toLowerCase();
      filtered = filtered.filter(customer => 
        customer.firstName.toLowerCase().includes(query) ||
        customer.lastName.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query) ||
        customer.phone.includes(query) ||
        customer.eventTitle.toLowerCase().includes(query)
      );
    }

    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());

    setFilteredCustomers(filtered);
  };

  const getUniqueEvents = () => {
    const eventMap = new Map();
    allCustomerData.forEach(customer => {
      if (!eventMap.has(customer.eventId)) {
        eventMap.set(customer.eventId, {
          id: customer.eventId,
          title: customer.eventTitle,
          customerCount: 0,
          completedOrders: 0
        });
      }
      const event = eventMap.get(customer.eventId);
      event.customerCount++;
      if (customer.isCompleted) {
        event.completedOrders++;
      }
    });
    return Array.from(eventMap.values()).sort((a, b) => b.customerCount - a.customerCount);
  };

  const handleExportAllCustomerData = () => {
    try {
      const result = exportFilteredCustomerData(
        allCustomerData,
        filteredCustomers,
        {
          status: customerStatusFilter,
          searchQuery: customerSearchQuery
        }
      );
      
      alert(`✅ Successfully exported ${result.recordCount} attendee records to ${result.filename}`);
    } catch (error) {
      console.error('Error exporting customer data:', error);
      alert('❌ Failed to export attendee data. Please try again.');
    }
  };

  const deleteCustomerData = (customerId: string) => {
    if (window.confirm('Are you sure you want to delete this attendee data?')) {
      customerService.deleteCustomerData(customerId);
      loadAllCustomerData();
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCustomerStatusColor = (status: CustomerCheckoutData['status']) => {
    switch (status) {
      case 'info_filled': return 'text-yellow-400 bg-yellow-400/20';
      case 'order_placed': return 'text-blue-400 bg-blue-400/20';
      case 'payment_completed': return 'text-green-400 bg-green-400/20';
      case 'cancelled': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const getCustomerStatusLabel = (status: CustomerCheckoutData['status']) => {
    switch (status) {
      case 'info_filled': return 'Info Filled';
      case 'order_placed': return 'Order Placed';
      case 'payment_completed': return 'Payment Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  // Effect to filter customer data when dependencies change
  useEffect(() => {
    filterCustomerData();
  }, [allCustomerData, selectedEventId, customerStatusFilter, customerSearchQuery]);

  // Account Management Functions
  const handleCreateAccount = () => {
    if (!newAccount.businessName || !newAccount.contactName || !newAccount.email) return;

    const account: PlatformAccount = {
      id: Date.now().toString(),
      type: newAccount.type,
      businessName: newAccount.businessName,
      contactName: newAccount.contactName,
      email: newAccount.email,
      phone: newAccount.phone,
      status: 'pending',
      subscriptionPlan: newAccount.subscriptionPlan,
      createdAt: new Date().toISOString(),
      businessInfo: {
        industry: newAccount.industry,
        website: newAccount.website,
        address: newAccount.address
      },
      metrics: {
        totalEvents: 0,
        totalRevenue: 0,
        totalTicketsSold: 0,
        averageEventSize: 0,
        monthlyActiveUsers: 0,
        conversionRate: 0
      },
      financial: {
        commissionRate: newAccount.commissionRate,
        totalCommissionEarned: 0,
        outstandingBalance: 0,
        payoutMethod: 'bank_transfer'
      },
      compliance: {
        kycStatus: 'pending',
        riskLevel: 'low'
      }
    };

    setAccounts(prev => [...prev, account]);
    setNewAccount({
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      type: 'client',
      subscriptionPlan: 'basic',
      industry: '',
      website: '',
      address: {
        street: '',
        city: '',
        country: 'Jordan',
        zipCode: ''
      },
      commissionRate: 5.0
    });
    setShowAccountModal(false);
  };

  const handleEditAccount = (account: PlatformAccount) => {
    setEditingAccount(account);
    setNewAccount({
      businessName: account.businessName,
      contactName: account.contactName,
      email: account.email,
      phone: account.phone,
      type: account.type,
      subscriptionPlan: account.subscriptionPlan,
      industry: account.businessInfo.industry,
      website: account.businessInfo.website || '',
      address: account.businessInfo.address,
      commissionRate: account.financial.commissionRate
    });
    setShowAccountModal(true);
  };

  const handleUpdateAccount = () => {
    if (!editingAccount) return;

    setAccounts(prev => prev.map(account => 
      account.id === editingAccount.id 
        ? { 
            ...account, 
            businessName: newAccount.businessName,
            contactName: newAccount.contactName,
            email: newAccount.email,
            phone: newAccount.phone,
            type: newAccount.type,
            subscriptionPlan: newAccount.subscriptionPlan,
            businessInfo: {
              ...account.businessInfo,
              industry: newAccount.industry,
              website: newAccount.website,
              address: newAccount.address
            },
            financial: {
              ...account.financial,
              commissionRate: newAccount.commissionRate
            }
          }
        : account
    ));
    
    setEditingAccount(null);
    setShowAccountModal(false);
    resetAccountForm();
  };

  const handleSuspendAccount = (accountId: string) => {
    if (window.confirm('Are you sure you want to suspend this account?')) {
      setAccounts(prev => prev.map(account => 
        account.id === accountId 
          ? { ...account, status: 'suspended' as const }
          : account
      ));
    }
  };

  const handleActivateAccount = (accountId: string) => {
    setAccounts(prev => prev.map(account => 
      account.id === accountId 
        ? { ...account, status: 'active' as const }
        : account
    ));
  };

  const handleDeleteAccount = (accountId: string) => {
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      setAccounts(prev => prev.filter(account => account.id !== accountId));
      // Also remove associated users
      setPlatformUsers(prev => prev.filter(user => user.accountId !== accountId));
    }
  };

  const handleAccessAdminDashboard = (accountId: string) => {
    // Navigate to Admin Dashboard with account context
    navigate(`/admin-dashboard?accountId=${accountId}`);
  };

  // User Management Functions
  const handleCreateUser = () => {
    if (!newUser.name || !newUser.email || !newUser.accountId) return;

    const user: PlatformUser = {
      id: Date.now().toString(),
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      accountId: newUser.accountId,
      isActive: true,
      createdAt: new Date().toISOString(),
      permissions: newUser.permissions
    };

    setPlatformUsers(prev => [...prev, user]);
    resetUserForm();
    setShowUserModal(false);
  };

  const handleEditUser = (user: PlatformUser) => {
    setEditingUser(user);
    setNewUser({
      name: user.name,
      email: user.email,
      role: user.role,
      accountId: user.accountId || '',
      password: '',
      permissions: user.permissions || []
    });
    setShowUserModal(true);
  };

  const handleUpdateUser = () => {
    if (!editingUser) return;

    setPlatformUsers(prev => prev.map(user => 
      user.id === editingUser.id 
        ? { 
            ...user, 
            name: newUser.name,
            email: newUser.email,
            role: newUser.role,
            accountId: newUser.accountId,
            permissions: newUser.permissions
          }
        : user
    ));
    
    setEditingUser(null);
    setShowUserModal(false);
    resetUserForm();
  };

  const handleToggleUserStatus = (userId: string) => {
    setPlatformUsers(prev => prev.map(user => 
      user.id === userId 
        ? { ...user, isActive: !user.isActive }
        : user
    ));
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setPlatformUsers(prev => prev.filter(user => user.id !== userId));
    }
  };

  // Support Ticket Functions
  const handleCreateTicket = () => {
    if (!newTicket.title || !newTicket.description) return;

    const ticket: SupportTicket = {
      id: Date.now().toString(),
      title: newTicket.title,
      description: newTicket.description,
      status: 'open',
      priority: newTicket.priority,
      category: newTicket.category,
      accountId: newTicket.accountId,
      createdBy: 'admin@etickets.com',
      assignedTo: newTicket.assignedTo,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: []
    };

    setSupportTickets(prev => [...prev, ticket]);
    resetTicketForm();
    setShowTicketModal(false);
  };

  const handleUpdateTicketStatus = (ticketId: string, status: SupportTicket['status']) => {
    setSupportTickets(prev => prev.map(ticket => 
      ticket.id === ticketId 
        ? { 
            ...ticket, 
            status,
            updatedAt: new Date().toISOString(),
            ...(status === 'resolved' && { resolvedAt: new Date().toISOString() })
          }
        : ticket
    ));
  };

  const handleDeleteTicket = (ticketId: string) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      setSupportTickets(prev => prev.filter(ticket => ticket.id !== ticketId));
    }
  };

  // Utility functions
  const resetAccountForm = () => {
    setNewAccount({
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      type: 'client',
      subscriptionPlan: 'basic',
      industry: '',
      website: '',
      address: {
        street: '',
        city: '',
        country: 'Jordan',
        zipCode: ''
      },
      commissionRate: 5.0
    });
  };

  const resetUserForm = () => {
    setNewUser({
      name: '',
      email: '',
      role: 'client',
      accountId: '',
      password: '',
      permissions: []
    });
  };

  const resetTicketForm = () => {
    setNewTicket({
      title: '',
      description: '',
      priority: 'medium',
      category: 'technical',
      accountId: '',
      assignedTo: ''
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  };

  const parseTimeAgo = (timeAgo: string): number => {
    if (timeAgo === 'Just now') return 0;
    const match = timeAgo.match(/(\d+)\s*(minute|hour|day|week|month|year)/);
    if (!match) return Infinity;
    const value = parseInt(match[1]);
    const unit = match[2];
    const multipliers: Record<string, number> = {
      minute: 60,
      hour: 3600,
      day: 86400,
      week: 604800,
      month: 2592000,
      year: 31536000
    };
    return value * (multipliers[unit] || 0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/20';
      case 'suspended': return 'text-red-400 bg-red-400/20';
      case 'pending': return 'text-yellow-400 bg-yellow-400/20';
      case 'inactive': return 'text-gray-400 bg-gray-400/20';
      default: return 'opacity-70 bg-white/10';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-400 bg-red-400/20';
      case 'high': return 'text-orange-400 bg-orange-400/20';
      case 'medium': return 'text-yellow-400 bg-yellow-400/20';
      case 'low': return 'text-green-400 bg-green-400/20';
      default: return 'opacity-70 bg-white/10';
    }
  };

  // Show loading state while checking authentication
  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen theme-transition flex items-center justify-center" style={{background: 'var(--liquid-gradient-bg)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-lg" style={{color: 'var(--liquid-text-primary)'}}>Loading master dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    navigate('/admin-login');
    return null;
  }

  // Show access denied only if loading is complete and user is not master/platform admin
  if (!authLoading && !isMaster && !isPlatformAdmin) {
    return (
      <div className="min-h-screen theme-transition flex items-center justify-center" style={{background: 'var(--liquid-gradient-bg)'}}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Access Denied</h1>
          <p className="mb-4" style={{color: 'var(--liquid-text-primary)'}}>You don't have permission to access the Master Dashboard.</p>
          <p className="mb-4 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Master Dashboard is for platform administrators only.</p>
          <Button
            onClick={() => navigate('/admin')}
            variant="glass"
          >
            Go to Admin Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      <div className="relative z-10 p-4 sm:p-6 pt-20 sm:pt-24 pb-20">
        {/* Enhanced Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div>
            {user && (
              <div className="mb-2 flex items-center space-x-3">
                <span className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                  {user.firstName} {user.lastName || ''}
                </span>
                {getCurrentMasterRole() && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white/80">
                    {getCurrentMasterRole()}
                  </span>
                )}
              </div>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Master Dashboard</h1>
            <p className="opacity-70 text-sm sm:text-base" style={{color: 'var(--liquid-text-primary)'}}>Platform-wide control and management center</p>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto justify-end">
            <Button
              onClick={() => navigate('/admin-dashboard')}
              variant="glass"
              size="sm"
              title="Access Admin Dashboard"
            >
              <ExternalLink size={16} className="mr-2" />
              Admin Panel
            </Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-1 sm:space-x-1 glass-strong rounded-lg p-1 mb-6 sm:mb-8 overflow-x-auto">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3, permission: () => true },
            { key: 'admin-management', label: 'Clients Accounts', icon: Building, permission: canManageAccounts },
            { key: 'master-users', label: 'Master Users', icon: Shield, permission: canManageMasterUsers },
            { key: 'customers', label: 'Attendees', icon: Users, permission: () => true },
            { key: 'ticket-generation', label: 'Generate Tickets', icon: Ticket, permission: canIssueTickets },
            { key: 'create-event', label: 'Create Event', icon: Calendar, permission: canCreateEvents },
            { key: 'add-venue', label: 'Add Venue', icon: Layout, permission: canCreateEvents },
            { key: 'support', label: 'Support', icon: Shield, permission: canManageSupport },
            { key: 'analytics', label: 'Analytics', icon: TrendingUp, permission: canAccessAnalytics },
            { key: 'system', label: 'System', icon: Server, permission: canManageSystem },
            { key: 'financials', label: 'Financials', icon: DollarSign, permission: canViewFinancials }
          ].filter(tab => tab.permission()).map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  activeTab === tab.key 
                    ? 'bg-white/20 glow' 
                    : 'opacity-70 hover:opacity-100 hover:bg-white/10'
                }`}
                style={{color: 'var(--liquid-text-primary)'}}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Company Selector - Only show when viewing client data */}
        {(activeTab === 'customers' || activeTab === 'create-event' || activeTab === 'add-venue') && (
          <div className="mb-6">
            <Card variant="glass" className="p-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium" style={{color: 'var(--liquid-text-primary)'}}>
                  View Data for Company:
                </label>
                <select
                  value={selectedCompanyName}
                  onChange={(e) => setSelectedCompanyName(e.target.value)}
                  className="flex-1 max-w-xs px-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                >
                  <option value="all" className="bg-white text-gray-900">All Companies</option>
                    {adminAccounts
                      .filter((acc, index, self) => 
                        index === self.findIndex(a => a.companyName === acc.companyName)
                      )
                      .map((account) => (
                        <option key={account.id} value={account.companyName} className="bg-white text-gray-900">
                          {account.companyName}
                        </option>
                      ))}
                </select>
              </div>
            </Card>
          </div>
        )}
      </div>

      <div className="px-6 pb-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Platform Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Accounts</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.accounts.total || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <Building size={24} className="text-blue-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.accounts.new || 0} this month
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Platform Revenue</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{formatCurrency(stats?.revenue.total || 0)}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <DollarSign size={24} className="text-green-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.revenue.growth || 0}% growth
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Events</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.events.total || 0}</p>
                  </div>
                  <div className="w-12 h-12 style={{backgroundColor: '#f9bb1020'}} rounded-2xl flex items-center justify-center">
                    <Calendar size={24} className="style={{color: '#f9bb10'}}" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  {stats?.events.thisMonth || 0} this month
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Platform Users</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.users.total?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-500/20 rounded-2xl flex items-center justify-center">
                    <Users size={24} className="text-orange-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-green-400 text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  +{stats?.users.newToday || 0} today
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>System Health</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{stats?.system.uptime || 0}%</p>
                  </div>
                  <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
                    <Activity size={24} className="text-red-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-yellow-400 text-sm">
                  <AlertTriangle size={16} className="mr-1" />
                  {stats?.system.alerts || 0} alerts
                </div>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card variant="glass" className="p-6">
              <h2 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Recent Platform Activity</h2>
              <div className="space-y-4">
                {(() => {
                  const activities: Array<{ type: string; message: string; time: string; icon: any }> = [];
                  
                  // Get recent admin accounts (sorted by creation date)
                  const recentAccounts = [...adminAccounts]
                    .sort((a, b) => new Date(b.createdAt || b.subscription?.startDate || '').getTime() - new Date(a.createdAt || a.subscription?.startDate || '').getTime())
                    .slice(0, 2);
                  
                  recentAccounts.forEach(account => {
                    const createdAt = new Date(account.createdAt || account.subscription?.startDate || '');
                    const timeAgo = getTimeAgo(createdAt);
                    activities.push({
                      type: 'account',
                      message: `New account "${account.companyName}" created`,
                      time: timeAgo,
                      icon: Building
                    });
                  });
                  
                  // Get recent events (sorted by creation date)
                  const recentEvents = [...allEvents]
                    .sort((a, b) => new Date(b.createdAt || b.date || '').getTime() - new Date(a.createdAt || a.date || '').getTime())
                    .slice(0, 2);
                  
                  recentEvents.forEach(event => {
                    const createdAt = new Date(event.createdAt || event.date || '');
                    const timeAgo = getTimeAgo(createdAt);
                    activities.push({
                      type: 'event',
                      message: `New event "${event.title}" created`,
                      time: timeAgo,
                      icon: Calendar
                    });
                  });
                  
                  // Get recent customer registrations
                  const recentCustomers = [...allCustomerData]
                    .sort((a, b) => {
                      const dateA = (a as any).submittedAt || (a as any).createdAt || '';
                      const dateB = (b as any).submittedAt || (b as any).createdAt || '';
                      return new Date(dateB).getTime() - new Date(dateA).getTime();
                    })
                    .slice(0, 1);
                  
                  recentCustomers.forEach(customer => {
                    const createdAt = new Date((customer as any).submittedAt || (customer as any).createdAt || '');
                    if (!isNaN(createdAt.getTime())) {
                      const timeAgo = getTimeAgo(createdAt);
                      const customerName = `${customer.firstName} ${customer.lastName}`.trim() || 'Customer';
                      activities.push({
                        type: 'user',
                        message: `New customer "${customerName}" registered`,
                        time: timeAgo,
                        icon: UserPlus
                      });
                    }
                  });
                  
                  // Sort all activities by time (most recent first) and take top 5
                  return activities
                    .sort((a, b) => {
                      const timeA = parseTimeAgo(a.time);
                      const timeB = parseTimeAgo(b.time);
                      return timeA - timeB;
                    })
                    .slice(0, 5)
                    .map((activity, index) => {
                      const Icon = activity.icon;
                      return (
                        <div key={index} className="flex items-center space-x-4 p-4 glass rounded-2xl">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <Icon size={20} className="text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="" style={{color: 'var(--liquid-text-primary)'}}>{activity.message}</p>
                            <p className="opacity-60 text-sm" style={{color: 'var(--liquid-text-primary)'}}>{activity.time}</p>
                          </div>
                        </div>
                      );
                    });
                })()}
                {(() => {
                  const recentAccounts = [...adminAccounts].sort((a, b) => new Date(b.createdAt || b.subscription?.startDate || '').getTime() - new Date(a.createdAt || a.subscription?.startDate || '').getTime()).slice(0, 2);
                  const recentEvents = [...allEvents].sort((a, b) => new Date(b.createdAt || b.date || '').getTime() - new Date(a.createdAt || a.date || '').getTime()).slice(0, 2);
                  const recentCustomers = [...allCustomerData].sort((a, b) => {
                    const dateA = (a as any).submittedAt || (a as any).createdAt || '';
                    const dateB = (b as any).submittedAt || (b as any).createdAt || '';
                    return new Date(dateB).getTime() - new Date(dateA).getTime();
                  }).slice(0, 1);
                  return recentAccounts.length === 0 && recentEvents.length === 0 && recentCustomers.length === 0;
                })() && (
                  <div className="text-center py-8 opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Admin Management Tab */}
        {activeTab === 'admin-management' && (
          <div className="space-y-8">
            <AdminManagement />
          </div>
        )}

        {/* Master Users Management Tab */}
        {activeTab === 'master-users' && canManageMasterUsers() && (
          <div className="space-y-8">
            <MasterUsersManagement />
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Platform Accounts</h2>
              <Button
                onClick={() => setShowAccountModal(true)}
                variant="glass"
                size="lg"
              >
                <Plus size={20} className="mr-2" />
                Create Account
              </Button>
            </div>

            {/* Accounts List */}
            <Card variant="glass" className="p-6">
              <div className="space-y-4">
                {accounts.map((account) => (
                  <div key={account.id} className="glass p-6 rounded-2xl">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center">
                          <Building size={24} className="text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-semibold  text-lg" style={{color: 'var(--liquid-text-primary)'}}>{account.businessName}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(account.status)}`}>
                              {account.status.toUpperCase()}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-medium style={{backgroundColor: '#f9bb1020'}} style={{color: '#f9bb10'}}">
                              {account.type.toUpperCase()}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                              {account.subscriptionPlan.toUpperCase()}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                            <div>
                              <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Contact Information</p>
                              <p className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>{account.contactName}</p>
                              <p className="opacity-60 text-sm" style={{color: 'var(--liquid-text-primary)'}}>{account.email}</p>
                              <p className="opacity-60 text-sm" style={{color: 'var(--liquid-text-primary)'}}>{account.phone}</p>
                            </div>
                            <div>
                              <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Business Metrics</p>
                              <p className="" style={{color: 'var(--liquid-text-primary)'}}>{account.metrics.totalEvents} Events</p>
                              <p className="text-green-400">{formatCurrency(account.metrics.totalRevenue)} Revenue</p>
                              <p className="text-blue-400">{account.metrics.totalTicketsSold.toLocaleString()} Tickets Sold</p>
                            </div>
                            <div>
                              <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Financial</p>
                              <p className="" style={{color: 'var(--liquid-text-primary)'}}>{account.financial.commissionRate}% Commission</p>
                              <p className="text-green-400">{formatCurrency(account.financial.totalCommissionEarned)} Earned</p>
                              <p className="text-yellow-400">{formatCurrency(account.financial.outstandingBalance)} Outstanding</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => handleAccessAdminDashboard(account.id)}
                          variant="secondary"
                          size="sm"
                          className="px-4 py-2 bg-blue-500/20 text-blue-400"
                          title="Access Admin Dashboard"
                        >
                          <ExternalLink size={14} className="mr-1" />
                          Dashboard
                        </Button>
                        <Button
                          onClick={() => handleEditAccount(account)}
                          variant="glass"
                          size="icon"
                          className="p-2 rounded-xl"
                        >
                          <Edit size={16} />
                        </Button>
                        {account.status === 'active' ? (
                          <Button
                            onClick={() => handleSuspendAccount(account.id)}
                            variant="glass"
                            size="icon"
                            className="p-2 rounded-xl"
                          >
                            <Ban size={16} className="text-red-400" />
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleActivateAccount(account.id)}
                            variant="glass"
                            size="icon"
                            className="p-2 rounded-xl"
                          >
                            <CheckCircle size={16} className="text-green-400" />
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDeleteAccount(account.id)}
                          variant="glass"
                          size="icon"
                          className="p-2 rounded-xl"
                        >
                          <Trash2 size={16} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && canManageSupport() && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Support Tickets</h2>
              <Button
                onClick={() => setShowTicketModal(true)}
                variant="glass"
                size="lg"
              >
                <Plus size={20} className="mr-2" />
                Create Ticket
              </Button>
            </div>

            {/* Support Tickets */}
            <Card variant="glass" className="p-6">
              <div className="space-y-4">
                {supportTickets.map((ticket) => {
                  const account = accounts.find(acc => acc.id === ticket.accountId);
                  return (
                    <div key={ticket.id} className="glass p-4 rounded-2xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{ticket.title}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority.toUpperCase()}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                              {ticket.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>{ticket.description}</p>
                          <div className="flex items-center space-x-4 text-xs opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                            <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                            <span>By: {ticket.createdBy}</span>
                            {account && <span>Account: {account.businessName}</span>}
                            <span>Category: {ticket.category}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <select
                            value={ticket.status}
                            onChange={(e) => handleUpdateTicketStatus(ticket.id, e.target.value as SupportTicket['status'])}
                            className="px-3 py-1 glass-input rounded text-sm bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                          >
                            <option value="open" className="bg-white text-gray-900">Open</option>
                            <option value="pending" className="bg-white text-gray-900">Pending</option>
                            <option value="resolved" className="bg-white text-gray-900">Resolved</option>
                            <option value="closed" className="bg-white text-gray-900">Closed</option>
                          </select>
                          <Button
                            onClick={() => handleDeleteTicket(ticket.id)}
                            variant="glass"
                            size="icon"
                            className="p-2 rounded"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && canManageSystem() && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>System Health & Monitoring</h2>
            
            {/* System Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>System Uptime</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{systemHealth?.uptime || 0}%</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <Activity size={24} className="text-green-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Performance</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{systemHealth?.performance || 0}%</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <Zap size={24} className="text-blue-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Active Alerts</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{systemHealth?.activeAlerts || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
                    <AlertTriangle size={24} className="text-red-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Components</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {systemHealth?.components ? 
                        `${Object.values(systemHealth.components).filter(c => c.status === 'operational').length}/${Object.keys(systemHealth.components).length}` 
                        : '0/0'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <CheckCircle size={24} className="text-green-400" />
                  </div>
                </div>
              </Card>
            </div>

            {/* System Components */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>System Components</h3>
              <div className="space-y-4">
                {Object.entries(systemHealth?.components || {}).map(([component, data]) => (
                  <div key={component} className="flex items-center justify-between p-4 glass rounded-2xl">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        data.status === 'operational' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                      }`}>
                        <Server size={20} className={data.status === 'operational' ? 'text-green-400' : 'text-yellow-400'} />
                      </div>
                      <div>
                        <h4 className="font-semibold  capitalize" style={{color: 'var(--liquid-text-primary)'}}>{component}</h4>
                        <p className={`text-sm ${data.status === 'operational' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {data.status.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>{data.responseTime}ms</p>
                      <p className="opacity-60 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Response Time</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && canAccessAnalytics() && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Platform Analytics</h2>
            
            {/* Overview Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Accounts</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{analytics?.overview.totalAccounts || 0}</p>
                    <p className="text-xs text-green-400 mt-1">+{analytics?.overview.newAccountsThisMonth || 0} this month</p>
                  </div>
                  <Building size={32} className="text-blue-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Revenue</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{formatCurrency(analytics?.overview.totalPlatformRevenue || 0)}</p>
                    <p className="text-xs text-green-400 mt-1">Commissions: {formatCurrency(analytics?.overview.totalCommissionEarned || 0)}</p>
                  </div>
                  <DollarSign size={32} className="text-green-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Events</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{analytics?.overview.totalEventsCreated || 0}</p>
                    <p className="text-xs text-blue-400 mt-1">Avg: {analytics?.overview.averageAccountValue ? Math.round(analytics.overview.averageAccountValue) : 0} per account</p>
                  </div>
                  <Calendar size={32} className="text-yellow-400 opacity-60" />
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Tickets Sold</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{analytics?.overview.totalTicketsSold || 0}</p>
                    <p className="text-xs text-purple-400 mt-1">Active: {analytics?.overview.activeAccounts || 0} accounts</p>
                  </div>
                  <Ticket size={32} className="text-purple-400 opacity-60" />
                </div>
              </Card>
            </div>

            {/* Growth Trends */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Growth Trends (Last 6 Months)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {analytics?.trends.accountGrowth.slice(-6).map((metric, index) => (
                  <div key={index} className="p-4 glass rounded-xl">
                    <p className="text-sm opacity-70 mb-2" style={{color: 'var(--liquid-text-primary)'}}>{metric.month}</p>
                    <p className="text-2xl font-bold mb-1" style={{color: 'var(--liquid-text-primary)'}}>{metric.value}</p>
                    <p className={`text-xs ${metric.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {metric.change >= 0 ? '+' : ''}{metric.change.toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Top Performers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card variant="glass" className="p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Top by Revenue</h3>
                <div className="space-y-3">
                  {analytics?.topPerformers.byRevenue.slice(0, 5).map((account, index) => (
                    <div key={account.id} className="flex items-center justify-between p-3 glass rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-sm" style={{color: 'var(--liquid-text-primary)'}}>{account.businessName}</p>
                          <p className="text-xs opacity-60" style={{color: 'var(--liquid-text-primary)'}}>{formatCurrency(account.metrics.totalRevenue)}</p>
                        </div>
                      </div>
                      <TrendingUp size={16} className="text-green-400" />
                    </div>
                  ))}
                  {(!analytics?.topPerformers.byRevenue || analytics.topPerformers.byRevenue.length === 0) && (
                    <p className="text-sm opacity-60 text-center py-4" style={{color: 'var(--liquid-text-primary)'}}>No data available</p>
                  )}
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Top by Events</h3>
                <div className="space-y-3">
                  {analytics?.topPerformers.byEvents.slice(0, 5).map((account, index) => (
                    <div key={account.id} className="flex items-center justify-between p-3 glass rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-sm" style={{color: 'var(--liquid-text-primary)'}}>{account.businessName}</p>
                          <p className="text-xs opacity-60" style={{color: 'var(--liquid-text-primary)'}}>{account.metrics.totalEvents} events</p>
                        </div>
                      </div>
                      <Calendar size={16} className="text-blue-400" />
                    </div>
                  ))}
                  {(!analytics?.topPerformers.byEvents || analytics.topPerformers.byEvents.length === 0) && (
                    <p className="text-sm opacity-60 text-center py-4" style={{color: 'var(--liquid-text-primary)'}}>No data available</p>
                  )}
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Top by Growth</h3>
                <div className="space-y-3">
                  {analytics?.topPerformers.byGrowth.slice(0, 5).map((account, index) => (
                    <div key={account.id} className="flex items-center justify-between p-3 glass rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-sm" style={{color: 'var(--liquid-text-primary)'}}>{account.businessName}</p>
                          <p className="text-xs opacity-60" style={{color: 'var(--liquid-text-primary)'}}>{account.metrics.conversionRate.toFixed(1)}% conversion</p>
                        </div>
                      </div>
                      <TrendingUp size={16} className="text-green-400" />
                    </div>
                  ))}
                  {(!analytics?.topPerformers.byGrowth || analytics.topPerformers.byGrowth.length === 0) && (
                    <p className="text-sm opacity-60 text-center py-4" style={{color: 'var(--liquid-text-primary)'}}>No data available</p>
                  )}
                </div>
              </Card>
            </div>

            {/* Revenue & Event Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card variant="glass" className="p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Revenue Trend (Last 6 Months)</h3>
                <div className="space-y-2">
                  {analytics?.trends.revenueGrowth.slice(-6).map((metric, index) => {
                    const maxRevenue = analytics?.trends.revenueGrowth.slice(-6).reduce((max, m) => Math.max(max, m.value), 0) || 1;
                    const widthPercent = Math.min(100, (metric.value / maxRevenue) * 100);
                    return (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>{metric.month}</span>
                        <div className="flex items-center space-x-3 flex-1 mx-4">
                          <div className="flex-1 bg-white/10 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                              style={{ width: `${widthPercent}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold w-20 text-right" style={{color: 'var(--liquid-text-primary)'}}>
                            {formatCurrency(metric.value)}
                          </span>
                        </div>
                        <span className={`text-xs w-16 text-right ${metric.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {metric.change >= 0 ? '+' : ''}{metric.change.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Event Growth (Last 6 Months)</h3>
                <div className="space-y-2">
                  {analytics?.trends.eventGrowth.slice(-6).map((metric, index) => {
                    const maxEvents = analytics?.trends.eventGrowth.slice(-6).reduce((max, m) => Math.max(max, m.value), 0) || 1;
                    const widthPercent = Math.min(100, (metric.value / maxEvents) * 100);
                    return (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>{metric.month}</span>
                        <div className="flex items-center space-x-3 flex-1 mx-4">
                          <div className="flex-1 bg-white/10 rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                              style={{ width: `${widthPercent}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold w-16 text-right" style={{color: 'var(--liquid-text-primary)'}}>
                            {metric.value}
                          </span>
                        </div>
                        <span className={`text-xs w-16 text-right ${metric.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {metric.change >= 0 ? '+' : ''}{metric.change.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* Ticket Sales Trend */}
            <Card variant="glass" className="p-6">
              <h3 className="text-lg font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Ticket Sales Trend (Last 6 Months)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {analytics?.trends.ticketSalesGrowth.slice(-6).map((metric, index) => (
                  <div key={index} className="p-4 glass rounded-xl text-center">
                    <p className="text-xs opacity-70 mb-2" style={{color: 'var(--liquid-text-primary)'}}>{metric.month}</p>
                    <p className="text-xl font-bold mb-1" style={{color: 'var(--liquid-text-primary)'}}>{metric.value}</p>
                    <p className={`text-xs ${metric.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {metric.change >= 0 ? '+' : ''}{metric.change.toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Financials Tab */}
        {activeTab === 'financials' && canViewFinancials() && (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Financial Overview</h2>
            <Card variant="glass" className="p-6">
              <div className="text-center py-12">
                <DollarSign size={64} className="mx-auto opacity-40 mb-4" style={{color: 'var(--liquid-text-primary)'}} />
                <h3 className="text-xl font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Financial Reports Coming Soon</h3>
                <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Platform revenue analytics, commission tracking, and financial reports will be available here.</p>
              </div>
            </Card>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Attendees Management</h2>
              <div className="flex items-center space-x-3">
                <Button
                  onClick={handleExportAllCustomerData}
                  disabled={filteredCustomers.length === 0}
                  variant="glass"
                  size="md"
                  title="Export attendees data to Excel"
                >
                  <Download size={16} className="mr-2" />
                  Export Data
                </Button>
                <Button
                  onClick={loadAllCustomerData}
                  variant="glass"
                  size="md"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Customer Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Attendees</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>{allCustomerData.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <Users size={24} className="text-blue-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Completed Orders</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {allCustomerData.filter(c => c.isCompleted).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                    <CheckCircle size={24} className="text-green-400" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Total Events</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {getUniqueEvents().length}
                    </p>
                  </div>
                  <div className="w-12 h-12 style={{backgroundColor: '#f9bb1020'}} rounded-2xl flex items-center justify-center">
                    <Calendar size={24} className="style={{color: '#f9bb10'}}" />
                  </div>
                </div>
              </Card>

              <Card variant="glass" className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>Conversion Rate</p>
                    <p className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                      {allCustomerData.length > 0 
                        ? Math.round((allCustomerData.filter(c => c.isCompleted).length / allCustomerData.length) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
                    <TrendingUp size={24} className="text-yellow-400" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Event Overview Cards */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Events Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getUniqueEvents().map((event) => (
                  <div key={event.id} className="glass p-6 rounded-2xl">
                    <h4 className="font-semibold  mb-3 line-clamp-2" style={{color: 'var(--liquid-text-primary)'}}>{event.title}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Total Attendees:</span>
                        <span className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>{event.customerCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Completed Orders:</span>
                        <span className="text-green-400 font-medium">{event.completedOrders}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Conversion Rate:</span>
                        <span className="text-yellow-400 font-medium">
                          {event.customerCount > 0 ? Math.round((event.completedOrders / event.customerCount) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => setSelectedEventId(event.id)}
                      variant="glass"
                      size="sm"
                      className="w-full mt-4"
                    >
                      View Attendees
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            {/* Filters and Search */}
            <Card variant="glass" className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search attendees..."
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 w-64"
                    />
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                  </div>
                  
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="px-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass min-w-48"
                  >
                    <option value="all" className="bg-white text-gray-900">All Events</option>
                    {getUniqueEvents().map((event) => (
                      <option key={event.id} value={event.id} className="bg-white text-gray-900">
                        {event.title} ({event.customerCount})
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={customerStatusFilter}
                    onChange={(e) => setCustomerStatusFilter(e.target.value as typeof customerStatusFilter)}
                    className="px-4 py-2 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="all" className="bg-white text-gray-900">All Status</option>
                    <option value="info_filled" className="bg-white text-gray-900">Info Filled</option>
                    <option value="order_placed" className="bg-white text-gray-900">Order Placed</option>
                    <option value="payment_completed" className="bg-white text-gray-900">Payment Completed</option>
                  </select>
                </div>

                <div className="opacity-70 text-sm text-center" style={{color: 'var(--liquid-text-primary)'}}>
                  Showing {filteredCustomers.length} of {allCustomerData.length} attendees
                  {selectedEventId !== 'all' && (
                    <div className="text-xs mt-1">
                      Event: {getUniqueEvents().find(e => e.id === selectedEventId)?.title}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Attendees List */}
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Attendees Data</h3>
                {filteredCustomers.length > 0 && (
                  <span className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                    💡 Use "Export Data" to download as Excel spreadsheet
                  </span>
                )}
              </div>
              
              {filteredCustomers.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={64} className="mx-auto opacity-40 mb-4" style={{color: 'var(--liquid-text-primary)'}} />
                  <h3 className="text-xl font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>No Attendees Data</h3>
                  <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Attendees checkout information will appear here when users fill out forms.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCustomers.map((customer) => (
                    <div key={customer.id} className="glass p-6 rounded-2xl">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Attendee Info */}
                          <div>
                            <h4 className="font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                              {customer.firstName} {customer.lastName}
                            </h4>
                            <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                              <div className="flex items-center">
                                <Mail size={14} className="mr-2" />
                                {customer.email}
                              </div>
                              <div className="flex items-center">
                                <Phone size={14} className="mr-2" />
                                {customer.phone}
                              </div>
                              <div className="flex items-center">
                                <Calendar size={14} className="mr-2" />
                                {formatDateTime(customer.submittedAt)}
                              </div>
                            </div>
                          </div>

                          {/* Event Info */}
                          <div>
                            <h5 className="font-medium  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Event Details</h5>
                            <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                              <div className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>{customer.eventTitle}</div>
                              <div className="flex items-center">
                                <CreditCard size={14} className="mr-2" />
                                {customer.paymentMethod.toUpperCase()}
                              </div>
                              {customer.orderData && (
                                <>
                                  <div>{customer.orderData.ticketType} × {customer.orderData.quantity}</div>
                                  <div className="font-medium text-green-400">
                                    ${customer.orderData.total.toFixed(2)}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Address Info */}
                          <div>
                            <h5 className="font-medium  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Address</h5>
                            <div className="space-y-1 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                              {customer.deliveryAddress && (
                                <div className="flex items-start">
                                  <MapPin size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <div>{customer.deliveryAddress}</div>
                                    {customer.deliveryCity && <div>{customer.deliveryCity}</div>}
                                  </div>
                                </div>
                              )}
                              {customer.billingAddress && !customer.deliveryAddress && (
                                <div className="flex items-start">
                                  <MapPin size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <div>{customer.billingAddress}</div>
                                    {customer.city && <div>{customer.city}, {customer.country}</div>}
                                  </div>
                                </div>
                              )}
                              {customer.deliveryNotes && (
                                <div className="text-xs bg-white/10 p-2 rounded">
                                  Note: {customer.deliveryNotes}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status and Actions */}
                        <div className="flex flex-col items-end space-y-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCustomerStatusColor(customer.status)}`}>
                            {getCustomerStatusLabel(customer.status)}
                          </span>
                          
                          <Button
                            onClick={() => deleteCustomerData(customer.id)}
                            variant="glass"
                            size="icon"
                            className="p-2 rounded-xl"
                            title="Delete attendee data"
                          >
                            <Trash2 size={16} className="text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Ticket Generation Tab */}
        {activeTab === 'ticket-generation' && canIssueTickets() && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Generate Tickets</h2>
                <p className="opacity-70 text-sm mt-2" style={{color: 'var(--liquid-text-primary)'}}>
                  Generate tickets by category and download as PDF. Generated tickets will be saved to Attendees Data.
                </p>
              </div>
            </div>

            {/* Filters */}
            <Card variant="glass" className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Filter by Event Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedEventForTicket('');
                    }}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  >
                    <option value="all">All Categories</option>
                    <option value="Music">Music</option>
                    <option value="Business">Business</option>
                    <option value="Sports">Sports</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Arts">Arts</option>
                    <option value="Food">Food & Drink</option>
                    <option value="Tech">Technology</option>
                    <option value="Festival">Festival</option>
                  </select>
                </div>

                <div>
                  <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Select Event</label>
                  <select
                    value={selectedEventForTicket}
                    onChange={(e) => {
                      setSelectedEventForTicket(e.target.value);
                      const event = availableEvents.find(ev => ev.id === e.target.value);
                      if (event) {
                        setTicketForm(prev => ({ ...prev, price: event.price || 0 }));
                      }
                    }}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  >
                    <option value="">Select an event</option>
                    {availableEvents
                      .filter(event => selectedCategory === 'all' || event.category === selectedCategory)
                      .map((event) => (
                        <option key={event.id} value={event.id} className="bg-white text-gray-900">
                          {event.title} - {event.category} ({new Date(event.date).toLocaleDateString()})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </Card>

            {/* Ticket Form */}
            {selectedEventForTicket && (
              <Card variant="glass" className="p-6">
                <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Ticket Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Customer Name *</label>
                    <input
                      type="text"
                      value={ticketForm.customerName}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, customerName: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Enter customer full name"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Customer Phone *</label>
                    <input
                      type="tel"
                      value={ticketForm.customerPhone}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, customerPhone: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="+962 7 1234 5678"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Ticket Category *</label>
                    <select
                      value={ticketForm.ticketType}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, ticketType: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    >
                      {TICKET_CATEGORIES.map((category) => (
                        <option key={category.name} value={category.name} className="bg-white text-gray-900">
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {ticketForm.ticketType && (
                      <div className="mt-2 flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: TICKET_CATEGORIES.find(c => c.name === ticketForm.ticketType)?.color || '#7C4DFF' }}
                        />
                        <span className="text-xs opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                          Category color preview
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={ticketForm.quantity}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Price per Ticket *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ticketForm.price}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Seat Info (Optional)</label>
                    <input
                      type="text"
                      value={ticketForm.seatInfo}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, seatInfo: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="e.g., Section A, Row 5, Seat 12"
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                    Total: ${(ticketForm.price * ticketForm.quantity).toFixed(2)}
                  </div>
                  <Button
                    onClick={handleGenerateTicket}
                    disabled={generatingTicket || !ticketForm.customerName || !ticketForm.customerPhone || !selectedEventForTicket}
                    variant="glass"
                    size="lg"
                    loading={generatingTicket}
                  >
                    <Download size={18} className="inline mr-2" />
                    Generate & Download PDF
                  </Button>
                </div>
              </Card>
            )}

            {/* Instructions */}
            {!selectedEventForTicket && (
              <Card variant="glass" className="p-6">
                <div className="text-center py-8">
                  <Ticket size={64} className="mx-auto mb-4 opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
                  <h3 className="text-lg font-semibold mb-2" style={{color: 'var(--liquid-text-primary)'}}>Select an Event to Generate Tickets</h3>
                  <p className="opacity-70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                    Choose a category and event above to start generating tickets
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Create Event Tab */}
        {activeTab === 'create-event' && canCreateEvents() && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Event Management</h2>
                <p className="opacity-70 text-sm mt-2" style={{color: 'var(--liquid-text-primary)'}}>
                  Create and manage events. Published events will appear on the website.
                </p>
              </div>
            </div>

            {/* Events List */}
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>All Events ({allEvents.length})</h3>
                  <Button
                    onClick={loadAllEvents}
                    variant="glass"
                    size="sm"
                    title="Refresh events list"
                  >
                    <RefreshCw size={16} />
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    setEditingEvent(null);
                    setSelectedAdminAccountId('');
                    setNewEvent({
                      title: '',
                      description: '',
                      date: '',
                      endDate: '',
                      time: '',
                      venue: '',
                      venueId: '',
                      category: 'Music',
                      price: 0,
                      capacity: 0,
                      image: '',
                      tags: [],
                      status: 'draft',
                      duration: '',
                      dresscode: '',
                      gallery: [],
                      location: '',
                      sections: [{ name: '', price: 0, seats: 0, benefits: [], description: '' }]
                    });
                  }}
                  variant="glass"
                  size="lg"
                >
                  <Plus size={20} className="mr-2" />
                  Create New Event
                </Button>
              </div>

              <div className="space-y-4">
                {allEvents.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar size={64} className="mx-auto mb-4 opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
                    <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>No events yet. Create your first event!</p>
                  </div>
                ) : (
                  allEvents.map((event) => (
                    <div key={event.id} className="glass p-6 rounded-2xl">
                      <div className="flex items-start space-x-4">
                        {event.image && (
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-20 h-20 rounded-xl object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg mb-2" style={{color: 'var(--liquid-text-primary)'}}>{event.title}</h3>
                              <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>{event.description}</p>
                              <div className="flex items-center space-x-4 text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                                <span className="flex items-center">
                                  <Calendar size={14} className="mr-1" />
                                  {event.date}
                                </span>
                                <span className="flex items-center">
                                  <Clock size={14} className="mr-1" />
                                  {event.time || 'TBA'}
                                </span>
                                <span className="flex items-center">
                                  <MapPin size={14} className="mr-1" />
                                  {event.venue || 'TBA'}
                                </span>
                                <span className="px-2 py-1 bg-primary-500/20 text-primary-300 rounded text-xs">
                                  {event.category}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  event.status === 'published' ? 'bg-green-500/20 text-green-300' :
                                  event.status === 'draft' ? 'bg-gray-500/20 text-gray-300' :
                                  'bg-red-500/20 text-red-300'
                                }`}>
                                  {event.status?.toUpperCase() || 'DRAFT'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {event.status !== 'published' && (
                                <Button
                                  onClick={() => handlePublishEvent(event.id)}
                                  variant="glass"
                                  size="sm"
                                  title="Publish event to website"
                                >
                                  <Globe size={16} className="mr-1" />
                                  Publish
                                </Button>
                              )}
                              <Button
                                onClick={() => handleEditEvent(event)}
                                variant="glass"
                                size="icon"
                                className="p-2 rounded-xl"
                                title="Edit event"
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                onClick={() => handleDeleteEvent(event.id)}
                                variant="glass"
                                size="icon"
                                className="p-2 rounded-xl"
                                title="Delete event"
                              >
                                <Trash2 size={16} className="text-red-400" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Create/Edit Event Form */}
            <Card variant="glass" className="p-6">
                <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h3>
                
                <div className="space-y-4">
                  {!editingEvent && (
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Company Name *</label>
                      <select
                        value={selectedAdminAccountId}
                        onChange={(e) => setSelectedAdminAccountId(e.target.value)}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        required
                      >
                        <option value="">Select a company...</option>
                        {adminAccounts
                          .filter(acc => acc.isActive)
                          .map((account) => (
                            <option key={account.id} value={account.id} className="bg-white text-gray-900">
                              {account.companyName}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs opacity-60 mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                        Select the client account this event belongs to
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Event Title *</label>
                      <input
                        type="text"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        placeholder="Enter event title"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Category *</label>
                      <select
                        value={newEvent.category}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      >
                        <option value="Music">Music</option>
                        <option value="Business">Business</option>
                        <option value="Sports">Sports</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Arts">Arts</option>
                        <option value="Food">Food & Drink</option>
                        <option value="Tech">Technology</option>
                        <option value="Festival">Festival</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Description</label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 resize-none h-24"
                      placeholder="Event description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>From Date *</label>
                      <input
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>To Date *</label>
                      <input
                        type="date"
                        value={newEvent.endDate}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, endDate: e.target.value }))}
                        min={newEvent.date}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Time</label>
                    <input
                      type="time"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Venue (Optional)</label>
                      <select
                        value={newEvent.venueId}
                        onChange={(e) => {
                          const selectedVenue = allVenues.find(v => v.id === e.target.value);
                          setNewEvent(prev => ({ 
                            ...prev, 
                            venueId: e.target.value || '',
                            venue: selectedVenue?.name || '',
                            capacity: selectedVenue?.capacity || 0
                          }));
                        }}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      >
                        <option value="">No Venue Selected</option>
                        {allVenues.map((venue) => (
                          <option key={venue.id} value={venue.id} className="bg-white text-gray-900">
                            {venue.name} (Capacity: {venue.capacity})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs opacity-60 mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                        💡 Create venues in "Add Venue" tab first to link them here
                      </p>
                    </div>
                    
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Ticket Price ($) *</label>
                      <input
                        type="number"
                        value={newEvent.price}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, price: Number(e.target.value) }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Capacity</label>
                      <input
                        type="number"
                        value={newEvent.capacity}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, capacity: Number(e.target.value) }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Status</label>
                      <select
                        value={newEvent.status}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, status: e.target.value as any }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Event Image URL</label>
                    <input
                      type="url"
                      value={newEvent.image}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, image: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* New Event Details Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Duration (e.g., 2.5 hours)</label>
                      <input
                        type="text"
                        value={newEvent.duration}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, duration: e.target.value }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        placeholder="e.g., 2.5 hours"
                      />
                    </div>
                    <div>
                      <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Dress Code</label>
                      <input
                        type="text"
                        value={newEvent.dresscode}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, dresscode: e.target.value }))}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        placeholder="e.g., Smart Casual"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Location Text (e.g., Amman, Jordan)</label>
                    <input
                      type="text"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="e.g., Amman, Jordan"
                    />
                  </div>

                  {/* Event Sections */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block font-medium" style={{color: 'var(--liquid-text-primary)'}}>Ticket Sections *</label>
                      <Button
                        onClick={() => {
                          setNewEvent(prev => ({
                            ...prev,
                            sections: [...prev.sections, { name: '', price: 0, seats: 0, benefits: [], description: '' }]
                          }));
                        }}
                        variant="glass"
                        size="sm"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Section
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      {newEvent.sections.map((section, index) => (
                        <div key={index} className="glass p-4 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium text-sm" style={{color: 'var(--liquid-text-primary)'}}>Section {index + 1}</span>
                            {newEvent.sections.length > 1 && (
                              <Button
                                onClick={() => {
                                  setNewEvent(prev => ({
                                    ...prev,
                                    sections: prev.sections.filter((_, i) => i !== index)
                                  }));
                                }}
                                variant="glass"
                                size="icon"
                                className="p-1 rounded"
                              >
                                <X size={16} className="text-red-400" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-4 mb-3">
                            <div>
                              <label className="block text-xs font-medium mb-1 opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Name</label>
                              <input
                                type="text"
                                value={section.name}
                                onChange={(e) => {
                                  const updatedSections = [...newEvent.sections];
                                  updatedSections[index].name = e.target.value;
                                  setNewEvent(prev => ({ ...prev, sections: updatedSections }));
                                }}
                                className="w-full px-3 py-2 glass-input rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20"
                                placeholder="e.g., VIP, General"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1 opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Price ($)</label>
                              <input
                                type="number"
                                value={section.price}
                                onChange={(e) => {
                                  const updatedSections = [...newEvent.sections];
                                  updatedSections[index].price = Number(e.target.value);
                                  setNewEvent(prev => ({ ...prev, sections: updatedSections }));
                                }}
                                className="w-full px-3 py-2 glass-input rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20"
                                min="0"
                                step="0.01"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1 opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Seats</label>
                              <input
                                type="number"
                                value={section.seats}
                                onChange={(e) => {
                                  const updatedSections = [...newEvent.sections];
                                  updatedSections[index].seats = Number(e.target.value);
                                  setNewEvent(prev => ({ ...prev, sections: updatedSections }));
                                }}
                                className="w-full px-3 py-2 glass-input rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20"
                                min="1"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Description / Benefits</label>
                            <textarea
                              value={section.description || ''}
                              onChange={(e) => {
                                const updatedSections = [...newEvent.sections];
                                updatedSections[index].description = e.target.value;
                                setNewEvent(prev => ({ ...prev, sections: updatedSections }));
                              }}
                              className="w-full px-3 py-2 glass-input rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20 resize-none"
                              placeholder="e.g., VIP access, Access to venue facilities, Merchandise discount, Digital souvenir photo"
                              rows={2}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-4 mt-8">
                  <button
                    onClick={() => {
                      setEditingEvent(null);
                      setNewEvent({
                        title: '',
                        description: '',
                        date: '',
                        endDate: '',
                        time: '',
                        venue: '',
                        venueId: '',
                        category: 'Music',
                        price: 0,
                        capacity: 0,
                        image: '',
                        tags: [],
                        status: 'draft',
                        duration: '',
                        dresscode: '',
                        gallery: [],
                        location: '',
                        sections: [{ name: '', price: 0, seats: 0, benefits: [], description: '' }]
                      });
                    }}
                    className="flex-1 px-6 py-3 glass rounded-xl font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                  >
                    Cancel
                  </button>
                  <Button
                    onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                    variant="glass"
                    size="lg"
                    className="flex-1"
                  >
                    <Save size={20} className="mr-2" />
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                </div>
            </Card>
          </div>
        )}

        {/* Add Venue Tab */}
        {activeTab === 'add-venue' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Venue Management</h2>
                <p className="opacity-70 text-sm mt-2" style={{color: 'var(--liquid-text-primary)'}}>
                  Create and manage venue layouts with sections and seating.
                </p>
              </div>
            </div>

            {/* Venues List */}
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>All Venues</h3>
                <Button
                  onClick={() => {
                    setEditingVenue(null);
                    setNewVenue({
                      name: '',
                      capacity: 0,
                      address: '',
                      city: '',
                      country: '',
                      phone: '',
                      email: '',
                      website: '',
                      description: '',
                      parkingInfo: '',
                      accessibilityInfo: '',
                      googleMapsUrl: ''
                    });
                  }}
                  variant="glass"
                  size="lg"
                >
                  <Plus size={20} className="mr-2" />
                  Add New Venue
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allVenues.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <Layout size={64} className="mx-auto mb-4 opacity-50" style={{color: 'var(--liquid-text-primary)'}} />
                    <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>No venues yet. Create your first venue!</p>
                  </div>
                ) : (
                  allVenues.map((venue) => (
                    <div key={venue.id} className="glass p-6 rounded-2xl">
                      <div className="w-full h-32 bg-white/10 rounded-xl mb-4 flex items-center justify-center">
                        <Layout size={48} className="opacity-40" style={{color: 'var(--liquid-text-primary)'}} />
                      </div>
                      <h4 className="font-semibold mb-2" style={{color: 'var(--liquid-text-primary)'}}>{venue.name}</h4>
                      <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>Capacity: {venue.capacity.toLocaleString()}</p>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleEditVenue(venue)}
                          variant="glass"
                          size="sm"
                          className="flex-1"
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDeleteVenue(venue.id)}
                          variant="glass"
                          size="sm"
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Create/Edit Venue Form */}
            <Card variant="glass" className="p-6">
              <h3 className="text-xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>
                {editingVenue ? 'Edit Venue Layout' : 'Create Venue Layout'}
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Venue Name *</label>
                    <input
                      type="text"
                      value={newVenue.name}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Enter venue name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Total Capacity *</label>
                    <input
                      type="number"
                      value={newVenue.capacity}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, capacity: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Description</label>
                  <textarea
                    value={newVenue.description || ''}
                    onChange={(e) => setNewVenue(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl focus-glass resize-none h-24" style={{color: 'var(--liquid-text-primary)'}}
                    placeholder="Describe the venue, its features, and amenities..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Address</label>
                    <input
                      type="text"
                      value={newVenue.address || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Street address"
                    />
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>City</label>
                    <input
                      type="text"
                      value={newVenue.city || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="City name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Country</label>
                    <input
                      type="text"
                      value={newVenue.country || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Country name"
                    />
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Phone</label>
                    <input
                      type="tel"
                      value={newVenue.phone || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="+962 7 1234 5678"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Email</label>
                    <input
                      type="email"
                      value={newVenue.email || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="venue@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Website</label>
                    <input
                      type="url"
                      value={newVenue.website || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, website: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Google Maps URL (Venue Location) *</label>
                  <input
                    type="url"
                    value={newVenue.googleMapsUrl || ''}
                    onChange={(e) => setNewVenue(prev => ({ ...prev, googleMapsUrl: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                  <p className="text-xs opacity-60 mt-1" style={{color: 'var(--liquid-text-primary)'}}>
                    💡 Get the embed URL from Google Maps (Share → Embed a map)
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Parking Information</label>
                    <textarea
                      value={newVenue.parkingInfo || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, parkingInfo: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 resize-none h-20"
                      placeholder="Parking availability, capacity, pricing..."
                    />
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Accessibility Information</label>
                    <textarea
                      value={newVenue.accessibilityInfo || ''}
                      onChange={(e) => setNewVenue(prev => ({ ...prev, accessibilityInfo: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200 resize-none h-20"
                      placeholder="Wheelchair access, elevators, accessible restrooms..."
                    />
                  </div>
                </div>

              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setEditingVenue(null);
                    setNewVenue({
                      name: '',
                      capacity: 0,
                      address: '',
                      city: '',
                      country: '',
                      phone: '',
                      email: '',
                      website: '',
                      description: '',
                      parkingInfo: '',
                      accessibilityInfo: '',
                      googleMapsUrl: ''
                    });
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <Button
                  onClick={editingVenue ? handleUpdateVenue : handleCreateVenue}
                  variant="glass"
                  size="lg"
                  className="flex-1"
                >
                  <Save size={20} className="mr-2" />
                  {editingVenue ? 'Update Venue' : 'Create Venue'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Account Creation/Edit Modal */}
        {showAccountModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  {editingAccount ? 'Edit Account' : 'Create New Account'}
                </h3>
                <button
                  onClick={() => {
                    setShowAccountModal(false);
                    setEditingAccount(null);
                    resetAccountForm();
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Business Name</label>
                    <input
                      type="text"
                      value={newAccount.businessName}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, businessName: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Enter business name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Contact Name</label>
                    <input
                      type="text"
                      value={newAccount.contactName}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, contactName: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Contact person name"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Email</label>
                    <input
                      type="email"
                      value={newAccount.email}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="business@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Phone</label>
                    <input
                      type="tel"
                      value={newAccount.phone}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="+962-6-123-4567"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Account Type</label>
                    <select
                      value={newAccount.type}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value as 'admin' | 'client' }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    >
                      <option value="client" className="bg-white text-gray-900">Client</option>
                      <option value="admin" className="bg-white text-gray-900">Admin</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Subscription Plan</label>
                    <select
                      value={newAccount.subscriptionPlan}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, subscriptionPlan: e.target.value as any }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    >
                      <option value="basic" className="bg-white text-gray-900">Basic</option>
                      <option value="pro" className="bg-white text-gray-900">Pro</option>
                      <option value="enterprise" className="bg-white text-gray-900">Enterprise</option>
                      <option value="custom" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Custom</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Commission Rate (%)</label>
                    <input
                      type="number"
                      value={newAccount.commissionRate}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, commissionRate: Number(e.target.value) }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      min="0"
                      max="50"
                      step="0.1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Industry</label>
                    <input
                      type="text"
                      value={newAccount.industry}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Entertainment, Business, etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Website</label>
                    <input
                      type="url"
                      value={newAccount.website}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, website: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Address</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={newAccount.address.street}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, address: { ...prev.address, street: e.target.value } }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Street address"
                    />
                    <input
                      type="text"
                      value={newAccount.address.city}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, address: { ...prev.address, city: e.target.value } }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={newAccount.address.country}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, address: { ...prev.address, country: e.target.value } }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Country"
                    />
                    <input
                      type="text"
                      value={newAccount.address.zipCode}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, address: { ...prev.address, zipCode: e.target.value } }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Zip Code"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowAccountModal(false);
                    setEditingAccount(null);
                    resetAccountForm();
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <Button
                  onClick={editingAccount ? handleUpdateAccount : handleCreateAccount}
                  variant="glass"
                  size="lg"
                  className="flex-1"
                >
                  <Save size={20} className="mr-2" />
                  {editingAccount ? 'Update Account' : 'Create Account'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* User Creation/Edit Modal */}
        {showUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                    resetUserForm();
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl   focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                    placeholder="Full name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl   focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value as any }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="client" className="bg-gray-900 text-white">Client</option>
                    <option value="admin" className="bg-gray-900 text-white">Admin</option>
                    <option value="platform_admin" className="bg-gray-900 text-white">Platform Admin</option>
                    <option value="master" className="bg-gray-900 text-white">Master</option>
                  </select>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Account</label>
                  <select
                    value={newUser.accountId}
                    onChange={(e) => setNewUser(prev => ({ ...prev, accountId: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                    required
                  >
                    <option value="" className="bg-gray-900 text-white">Select Account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id} className="bg-gray-900 text-white">
                        {account.businessName}
                      </option>
                    ))}
                  </select>
                </div>
                
                {!editingUser && (
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Password</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                      placeholder="Temporary password"
                      required={!editingUser}
                    />
                  </div>
                )}
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                    resetUserForm();
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <Button
                  onClick={editingUser ? handleUpdateUser : handleCreateUser}
                  variant="glass"
                  size="lg"
                  className="flex-1"
                >
                  <Save size={20} className="mr-2" />
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Support Ticket Modal */}
        {showTicketModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <Card variant="glass" className="p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Create Support Ticket</h3>
                <button
                  onClick={() => {
                    setShowTicketModal(false);
                    resetTicketForm();
                  }}
                  className="opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Title</label>
                  <input
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl   focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                    placeholder="Brief description of the issue"
                    required
                  />
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Description</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl   focus-glass resize-none h-24" style={{color: 'var(--liquid-text-primary)'}}
                    placeholder="Detailed description of the issue"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Priority</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    >
                      <option value="low" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Low</option>
                      <option value="medium" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Medium</option>
                      <option value="high" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>High</option>
                      <option value="urgent" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Urgent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Category</label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, category: e.target.value as any }))}
                      className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    >
                      <option value="technical" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Technical</option>
                      <option value="billing" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Billing</option>
                      <option value="account" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Account</option>
                      <option value="feature_request" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Feature Request</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Account</label>
                  <select
                    value={newTicket.accountId}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, accountId: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl  bg-white/10 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                  >
                    <option value="" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Select Account (Optional)</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id} className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>
                        {account.businessName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-8">
                <button
                  onClick={() => {
                    setShowTicketModal(false);
                    resetTicketForm();
                  }}
                  className="flex-1 px-6 py-3 glass rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
                >
                  Cancel
                </button>
                <Button
                  onClick={handleCreateTicket}
                  variant="glass"
                  size="lg"
                  className="flex-1"
                >
                  <Save size={20} className="mr-2" />
                  Create Ticket
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterDashboard; 